import {toRomanNumeral} from 'common/HelperFunctions'
import FilterSelect from 'common/components/FilterSelect'
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'

import {
  getSchoolSystems,
  getSchoolSystemsDetails,
} from '../../../../../../setup/redux/Actions/CsvImport/csvImport'
import {WizardData} from '../index'
import {
  normalizeSchoolSystemDetailsResponse,
  readStoredSchoolSystemDetails,
  writeStoredSchoolSystemDetails,
} from '../schoolSystemDetailsCache'
import TagRemoveIcon from './TagRemoveIcon'
import WizardDatePicker from './WizardDatePicker'

const WEEKDAYS = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const

export {WEEKDAYS}

export const DAY_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const

export function formatTime24to12(time24: string): string {
  if (!time24 || !time24.includes(':')) return time24
  const [h, m] = time24.split(':').map(Number)
  const h12 = h % 12 || 12
  const ampm = h < 12 ? 'AM' : 'PM'
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`
}

export function timeToMinutes(time24: string): number {
  if (!time24 || !time24.includes(':')) return 0
  const [h, m] = time24.split(':').map(Number)
  return h * 60 + m
}

function minutesToTime24(mins: number): string {
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** Compute a 30-min break starting at the midpoint of the institute's daily schedule */
export function getMidpointBreak(timingsArr: any[]): {startTime: string; endTime: string} {
  const BREAK_DURATION = 30
  if (!timingsArr || timingsArr.length === 0) return {startTime: '', endTime: ''}
  // Use the earliest start and latest end across all days
  let minStart = Infinity
  let maxEnd = -Infinity
  for (const t of timingsArr) {
    const s = timeToMinutes(t.startTime)
    const e = timeToMinutes(t.endTime)
    if (s < minStart) minStart = s
    if (e > maxEnd) maxEnd = e
  }
  if (minStart >= maxEnd) return {startTime: '', endTime: ''}
  const mid = Math.floor((minStart + maxEnd) / 2)
  const breakStart = mid
  const breakEnd = breakStart + BREAK_DURATION
  return {startTime: minutesToTime24(breakStart), endTime: minutesToTime24(breakEnd)}
}

export function getSeasonFromCurrentDate(): {name: string; start: string; end: string} {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth()

  // Start: 1st of current month
  const start = `${year}-${String(month + 1).padStart(2, '0')}-01`

  // End: last day of the month 3 months from now
  const endDate = new Date(year, month + 4, 0) // day 0 of month+4 = last day of month+3
  const end = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`

  const SEASON_NAMES = [
    'Winter',
    'Spring',
    'Spring',
    'Spring',
    'Spring',
    'Summer',
    'Summer',
    'Summer',
    'Autumn',
    'Autumn',
    'Autumn',
    'Winter',
  ]
  const name = SEASON_NAMES[month]

  return {name, start, end}
}

export function normalizeDay(day?: string) {
  return String(day ?? '')
    .toUpperCase()
    .trim()
}

export const DEFAULT_TIMING_START = '08:00'
export const DEFAULT_TIMING_END = '14:00'
export const DEFAULT_LECTURE_DURATION = 30
// Monday–Friday come preselected in the timing modal
export const DEFAULT_TIMING_DAYS = WEEKDAYS.slice(0, 5)
// native date inputs accept years up to 275760 unless bounded — any year is fine, but keep it 4 digits
const SESSION_DATE_MIN = '0001-01-01'
const SESSION_DATE_MAX = '9999-12-31'
export const DEFAULT_BRANCH_NAME = 'Main Branch'
export const DEFAULT_SUBJECT_FREQUENCY = 5

const BASIC_SETUP_STEPS = [
  'branches',
  'curriculum',
  'levels',
  'duration',
  'venues',
  'timings',
  'frequency',
] as const

// 'breaks' is modal-only: the Institution Setup review page hosts break timings as
// their own section, so it never appears in a linear steps flow.
type Step2SubStep = (typeof BASIC_SETUP_STEPS)[number] | 'breaks'

interface Props {
  data: WizardData
  /**
   * `options.prefill` marks a write this screen makes on the user's behalf (the default branch
   * row, the default subject frequency) rather than one the user made. The wizard uses it to keep
   * those defaults out of the preferences buckets the onboarding funnel is inferred from — see
   * `updateData` in ../index.
   */
  updateData: (data: Partial<WizardData>, options?: {prefill?: boolean}) => void
  isDark: boolean
  // Controls which sub-steps are shown and in what order.
  // Example: CoreSetup vs Step3Curriculum embedded setup.
  steps?: ReadonlyArray<Step2SubStep>
  // UI-only props used by `Step3Curriculum` (safe to ignore for CoreSetup).
  extraStepperLabels?: string[]
  initialSubStep?: number
  onNext?: () => void | Promise<void>
  /** Called when the user presses Back on the first sub-step (exit to the previous wizard step). */
  onBack?: () => void
  onPartialSubmit?: (fields: any[], manualData?: WizardData['manualData'], cb?: () => void) => void
  isSubmitting?: boolean
  onAutofillLoadingChange?: (isLoading: boolean) => void
  /** Reports the active sub-step field (e.g. 'branches', 'levels') as the user navigates,
   *  so the parent can keep the URL in sync. */
  onActiveSubStepChange?: (field: string) => void
  /** Hide the horizontal sub-step progress bar (single-section modal hosting). */
  hideStepper?: boolean
  /** i18n id for the advance button label; defaults to the "Next Step" footer label. */
  nextLabelId?: string
}

const Step2BasicSetup: FC<Props> = ({
  data,
  updateData,
  isDark,
  steps,
  initialSubStep,
  extraStepperLabels,
  onNext,
  onBack,
  onPartialSubmit,
  isSubmitting = false,
  onAutofillLoadingChange,
  onActiveSubStepChange,
  hideStepper = false,
  nextLabelId,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const branches = data.manualData?.branches ?? []
  const stepsToUse = steps ?? BASIC_SETUP_STEPS
  const initialActiveSubStep = Math.max(
    0,
    Math.min(initialSubStep ?? 0, Math.max(0, stepsToUse.length - 1))
  )
  const [activeSubStep, setActiveSubStep] = useState(initialActiveSubStep)
  const activeField = stepsToUse[activeSubStep]
  useEffect(() => {
    if (activeField) onActiveSubStepChange?.(activeField)
  }, [activeField, onActiveSubStepChange])
  // Auto-focus the branch input when arriving at the branches sub-step so the
  // user can start typing immediately without having to click first.
  useEffect(() => {
    if (activeField !== 'branches') return
    const t = setTimeout(() => document.getElementById('step2-branch-input')?.focus(), 50)
    return () => clearTimeout(t)
  }, [activeField])
  useEffect(() => {
    if (activeField !== 'branches') return
    const existing = data.manualData?.branches ?? []
    if (existing.length > 0) {
      defaultBranchInitializedRef.current = true
      return
    }
    if (defaultBranchInitializedRef.current) return
    defaultBranchInitializedRef.current = true
    autoAddedDefaultBranchRef.current = true
    updateData(
      {
        manualData: {
          ...data.manualData,
          branches: [DEFAULT_BRANCH_NAME],
        },
      },
      {prefill: true}
    )
  }, [activeField, data.manualData, updateData])
  useEffect(() => {
    if (activeField !== 'frequency') return
    const freq = data.manualData?.defaultSubjectFrequency
    if (freq != null && freq > 0) {
      defaultFrequencyInitializedRef.current = true
      return
    }
    if (defaultFrequencyInitializedRef.current) return
    defaultFrequencyInitializedRef.current = true
    updateData(
      {
        manualData: {
          ...data.manualData,
          defaultSubjectFrequency: DEFAULT_SUBJECT_FREQUENCY,
          defaultFrequencyPer: data.manualData?.defaultFrequencyPer ?? 'week',
        },
      },
      {prefill: true}
    )
  }, [activeField, data.manualData, updateData])
  const [maxSubStepReached, setMaxSubStepReached] = useState(initialActiveSubStep)
  const [inputValue, setInputValue] = useState('')
  const [venueInputValue, setVenueInputValue] = useState('')
  const [fetchedSystems, setFetchedSystems] = useState<any[]>([])
  const [isLoadingSystems, setIsLoadingSystems] = useState(false)
  const [curriculumSearch, setCurriculumSearch] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [otherEditing, setOtherEditing] = useState(false)
  const [otherValue, setOtherValue] = useState('')
  const otherInputRef = useRef<HTMLInputElement | null>(null)
  const [completing, setCompleting] = useState(false)
  const lastFetchedCurriculumsRef = useRef<string[]>([])
  const manualDataRef = useRef(data.manualData)
  const fetchInProgressRef = useRef(false)
  manualDataRef.current = data.manualData
  const [branchInlineError, setBranchInlineError] = useState<string | null>(null)
  const defaultBranchInitializedRef = useRef(false)
  // True only while the branch list holds the untouched auto-added placeholder,
  // so the user's first typed branch replaces it instead of appending.
  const autoAddedDefaultBranchRef = useRef(false)
  const defaultFrequencyInitializedRef = useRef(false)
  const [expandedCurriculum, setExpandedCurriculum] = useState<Set<string>>(new Set())
  const [levelInputsByCurriculum, setLevelInputsByCurriculum] = useState<Record<string, string>>({})
  const [levelInputsByCurriculumPerBranch, setLevelInputsByCurriculumPerBranch] = useState<
    Record<string, Record<string, string>>
  >({})

  const selectedCurriculums = useMemo(
    () => data.manualData?.curriculums ?? [],
    [data.manualData?.curriculums]
  )

  const filteredSystems = useMemo(() => {
    const searchTerm = curriculumSearch.trim().toLowerCase()

    return fetchedSystems.filter((system) => {
      const systemName = String(system?.system ?? '').toLowerCase()
      const countryName = String(system?.country ?? '').toLowerCase()

      if (!searchTerm) return true
      return systemName.includes(searchTerm) || countryName.includes(searchTerm)
    })
  }, [curriculumSearch, fetchedSystems])

  useEffect(() => {
    if (activeField === 'curriculum' && fetchedSystems.length === 0) {
      setIsLoadingSystems(true)
      dispatch(
        getSchoolSystems(
          (res: any) => {
            setFetchedSystems(res?.result ?? [])
            setIsLoadingSystems(false)
          },
          () => {
            setIsLoadingSystems(false)
          }
        )
      )
    }
  }, [activeField, dispatch, fetchedSystems.length])

  useEffect(() => {
    // Fetch school-system-details only once per selected curriculum set.
    // After that, user edits must not trigger a refetch or overwrite.
    const sameSet =
      lastFetchedCurriculumsRef.current.length === selectedCurriculums.length &&
      selectedCurriculums.every((c) => lastFetchedCurriculumsRef.current.includes(c))
    const alreadyFetchedForThisSet = sameSet && lastFetchedCurriculumsRef.current.length > 0
    const shouldFetch =
      activeField === 'levels' &&
      selectedCurriculums.length > 0 &&
      !alreadyFetchedForThisSet &&
      !fetchInProgressRef.current

    if (!shouldFetch) return

    fetchInProgressRef.current = true
    onAutofillLoadingChange?.(true)
    dispatch(
      getSchoolSystemsDetails(
        selectedCurriculums,
        (res: any) => {
          fetchInProgressRef.current = false
          const systemsData = res?.result ?? []
          const normalizedDetails = normalizeSchoolSystemDetailsResponse(systemsData)
          const cachedDetails = readStoredSchoolSystemDetails()
          const nextCachedDetails = {...cachedDetails}
          // Use latest state from ref so we never overwrite user edits with stale closure data
          const latestManualData = manualDataRef.current
          const newLevelsByCurriculum: Record<string, any> = {
            ...(latestManualData?.levelsByCurriculum ?? {}),
          }

          selectedCurriculums.forEach((curriculumName) => {
            if (normalizedDetails[curriculumName]) {
              nextCachedDetails[curriculumName] = normalizedDetails[curriculumName]
            }
          })

          Object.keys(nextCachedDetails).forEach((curriculumName) => {
            if (!selectedCurriculums.includes(curriculumName)) {
              delete nextCachedDetails[curriculumName]
            }
          })

          systemsData.forEach((systemObj: any) => {
            const currName = systemObj.system
            if (systemObj.levels && Array.isArray(systemObj.levels)) {
              const curriculumLevels: any[] = []
              systemObj.levels.forEach((lvl: any) => {
                if (lvl.level && !curriculumLevels.find((al) => al.name === lvl.level)) {
                  curriculumLevels.push({
                    name: lvl.level,
                    id: Math.random().toString(36).substr(2, 9),
                  })
                }
              })

              if (curriculumLevels.length > 0) {
                const existingCurriculumData = newLevelsByCurriculum[currName] ?? {}
                const hasExistingLevels =
                  Array.isArray(existingCurriculumData.levels) &&
                  existingCurriculumData.levels.length > 0
                const hasExistingLevelsPerBranch =
                  existingCurriculumData.levelsPerBranch &&
                  Object.values(existingCurriculumData.levelsPerBranch).some(
                    (arr: any) => Array.isArray(arr) && arr.length > 0
                  )
                // Only apply API data when we have no user/cached data for this curriculum
                if (hasExistingLevels || hasExistingLevelsPerBranch) {
                  return
                }

                const branchList = latestManualData?.branches ?? []
                const newLevelsPerBranch: Record<string, string[]> = {}
                const levelNames = curriculumLevels.map((l) => l.name)
                for (const b of branchList) {
                  newLevelsPerBranch[b] = existingCurriculumData.levelsPerBranch?.[b] ?? [
                    ...levelNames,
                  ]
                }

                newLevelsByCurriculum[currName] = {
                  ...existingCurriculumData,
                  levels: curriculumLevels,
                  levelsSameAcrossBranches: existingCurriculumData.levelsSameAcrossBranches ?? true,
                  levelsPerBranch: newLevelsPerBranch,
                  levelDetails:
                    existingCurriculumData.levelDetails ?? normalizedDetails[currName] ?? {},
                }
              }
            }
          })

          writeStoredSchoolSystemDetails(nextCachedDetails)
          lastFetchedCurriculumsRef.current = [...selectedCurriculums]
          updateData({
            manualData: {
              ...latestManualData,
              levelsByCurriculum: newLevelsByCurriculum,
            },
          })
          setTimeout(() => onAutofillLoadingChange?.(false), 0)
        },
        () => {
          fetchInProgressRef.current = false
          onAutofillLoadingChange?.(false)
        }
      )
    )
    return () => {
      if (fetchInProgressRef.current) {
        onAutofillLoadingChange?.(false)
      }
    }
  }, [activeField, selectedCurriculums, dispatch, updateData, onAutofillLoadingChange])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (otherEditing) {
      setTimeout(() => otherInputRef.current?.focus(), 0)
    }
  }, [otherEditing])

  const handleToggleCurriculum = (system: string) => {
    const current = [...selectedCurriculums]
    const idx = current.indexOf(system)
    if (idx > -1) {
      current.splice(idx, 1)
    } else {
      current.push(system)
    }
    updateData({
      manualData: {
        ...data.manualData,
        curriculums: current,
      },
    })
  }

  const handleConfirmOther = () => {
    const name = otherValue.trim()
    if (!name) {
      setOtherEditing(false)
      setOtherValue('')
      return
    }
    const existing = data.manualData?.curriculums ?? []
    if (existing.some((c) => c.toLowerCase() === name.toLowerCase())) {
      setOtherEditing(false)
      setOtherValue('')
      return
    }
    // Recorded as a custom name too, so Setup Basics keeps the pill when it is de-selected.
    const existingCustom = data.manualData?.customCurriculums ?? []
    updateData({
      manualData: {
        ...data.manualData,
        curriculums: [...existing, name],
        ...(existingCustom.some((c) => c.toLowerCase() === name.toLowerCase())
          ? {}
          : {customCurriculums: [...existingCustom, name]}),
      },
    })
    setOtherEditing(false)
    setOtherValue('')
  }

  useEffect(() => {
    const dn = data.manualData?.durationName ?? ''
    const ds = data.manualData?.durationStartDate ?? ''
    const de = data.manualData?.durationEndDate ?? ''
    if (!dn.trim() && !ds && !de) {
      const season = getSeasonFromCurrentDate()
      updateData({
        manualData: {
          ...data.manualData,
          durationName: season.name,
          durationStartDate: season.start,
          durationEndDate: season.end,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount when duration is empty
  }, [])

  const advanceSubStep = () => {
    const currentFields = activeField
    let fieldsToSync: string[] = []

    if (currentFields === 'branches') fieldsToSync = ['branches']
    else if (currentFields === 'levels') fieldsToSync = ['levels', 'duration', 'timings']
    else if (currentFields === 'duration') fieldsToSync = []
    else if (currentFields === 'timings') fieldsToSync = []

    const doTransition = () => {
      setCompleting(true)
      setTimeout(() => {
        setCompleting(false)
        if (activeSubStep < stepsToUse.length - 1) {
          const next = activeSubStep + 1
          setActiveSubStep(next)
          setMaxSubStepReached((m) => Math.max(m, next))
        } else {
          Promise.resolve(onNext?.()).catch(() => undefined)
        }
      }, 650)
    }

    if (fieldsToSync.length > 0 && onPartialSubmit) {
      onPartialSubmit(fieldsToSync, data.manualData, doTransition)
    } else {
      doTransition()
    }
  }

  const goBackSubStep = () => {
    if (activeSubStep > 0) {
      setActiveSubStep(activeSubStep - 1)
    } else {
      onBack?.()
    }
  }

  const handleAddBranch = useCallback(() => {
    const name = inputValue.trim()
    if (!name) return
    // Branch names may only contain letters, numbers, spaces and hyphens.
    if (!/^[\p{L}\p{N}\s-]+$/u.test(name)) {
      setBranchInlineError('Branch name cannot contain special characters.')
      return
    }
    const existing = data.manualData?.branches ?? []
    if (
      autoAddedDefaultBranchRef.current &&
      existing.length === 1 &&
      existing[0] === DEFAULT_BRANCH_NAME &&
      existing[0].toLowerCase() !== name.toLowerCase()
    ) {
      autoAddedDefaultBranchRef.current = false
      setBranchInlineError(null)
      updateData({
        manualData: {
          ...data.manualData,
          branches: [name],
        },
      })
      setInputValue('')
      return
    }
    if (existing.some((b) => b.toLowerCase() === name.toLowerCase())) {
      setBranchInlineError(
        intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCH_DUPLICATE_ERROR'})
      )
      setInputValue('')
      return
    }
    autoAddedDefaultBranchRef.current = false
    setBranchInlineError(null)
    updateData({
      manualData: {
        ...data.manualData,
        branches: [...existing, name],
      },
    })
    setInputValue('')
  }, [inputValue, data.manualData, updateData, intl])

  const handleRemoveBranch = useCallback(
    (idx: number) => {
      const existing = data.manualData?.branches ?? []
      autoAddedDefaultBranchRef.current = false
      updateData({
        manualData: {
          ...data.manualData,
          branches: existing.filter((_, i) => i !== idx),
        },
      })
    },
    [data.manualData, updateData]
  )

  const handleAddLevelToCurriculum = useCallback(
    (curriculumName: string, inputValue: string, setInputValue: (val: string) => void) => {
      const name = inputValue.trim()
      if (!name) return
      const curriculumData = data.manualData?.levelsByCurriculum?.[curriculumName]
      const existing = curriculumData?.levels ?? []

      setBranchInlineError(null)

      const updatedCurriculum = {
        levels: [...existing, {name, id: Math.random().toString(36).substr(2, 9)}],
        levelsSameAcrossBranches: curriculumData?.levelsSameAcrossBranches ?? true,
        levelsPerBranch: curriculumData?.levelsPerBranch ?? {},
      }

      updateData({
        manualData: {
          ...data.manualData,
          levelsByCurriculum: {
            ...(data.manualData?.levelsByCurriculum ?? {}),
            [curriculumName]: updatedCurriculum,
          },
        },
      })
      setInputValue('')
    },
    [data.manualData, updateData]
  )

  const handleRemoveLevelFromCurriculum = useCallback(
    (curriculumName: string, idx: number) => {
      const curriculumData = data.manualData?.levelsByCurriculum?.[curriculumName]
      const existing = curriculumData?.levels ?? []

      const updatedCurriculum = {
        levels: existing.filter((_, i) => i !== idx),
        levelsSameAcrossBranches: curriculumData?.levelsSameAcrossBranches ?? true,
        levelsPerBranch: curriculumData?.levelsPerBranch ?? {},
      }

      updateData({
        manualData: {
          ...data.manualData,
          levelsByCurriculum: {
            ...(data.manualData?.levelsByCurriculum ?? {}),
            [curriculumName]: updatedCurriculum,
          },
        },
      })
    },
    [data.manualData, updateData]
  )

  const handleAddLevelToCurriculumBranch = useCallback(
    (
      curriculumName: string,
      branchName: string,
      inputValue: string,
      setInputValue: (val: string) => void
    ) => {
      const val = inputValue.trim()
      if (!val) return
      const curriculumData = data.manualData?.levelsByCurriculum?.[curriculumName]
      const existing = curriculumData?.levelsPerBranch?.[branchName] ?? []

      const updatedCurriculum = {
        levels: curriculumData?.levels ?? [],
        levelsSameAcrossBranches: curriculumData?.levelsSameAcrossBranches ?? false,
        levelsPerBranch: {
          ...(curriculumData?.levelsPerBranch ?? {}),
          [branchName]: [...existing, val],
        },
      }

      updateData({
        manualData: {
          ...data.manualData,
          levelsByCurriculum: {
            ...(data.manualData?.levelsByCurriculum ?? {}),
            [curriculumName]: updatedCurriculum,
          },
        },
      })
      setInputValue('')
    },
    [data.manualData, updateData]
  )

  const handleRemoveLevelFromCurriculumBranch = useCallback(
    (curriculumName: string, branchName: string, idx: number) => {
      const curriculumData = data.manualData?.levelsByCurriculum?.[curriculumName]
      const existing = curriculumData?.levelsPerBranch?.[branchName] ?? []

      const updatedCurriculum = {
        levels: curriculumData?.levels ?? [],
        levelsSameAcrossBranches: curriculumData?.levelsSameAcrossBranches ?? false,
        levelsPerBranch: {
          ...(curriculumData?.levelsPerBranch ?? {}),
          [branchName]: existing.filter((_, i) => i !== idx),
        },
      }

      updateData({
        manualData: {
          ...data.manualData,
          levelsByCurriculum: {
            ...(data.manualData?.levelsByCurriculum ?? {}),
            [curriculumName]: updatedCurriculum,
          },
        },
      })
    },
    [data.manualData, updateData]
  )

  const venues = data.manualData?.venues ?? []
  const venuesAutoCount = data.manualData?.venuesAutoCount ?? 0
  const venueInputMode = venues.length > 0 ? 'manual' : venuesAutoCount > 0 ? 'auto' : null

  const handleAddVenue = useCallback(() => {
    const name = venueInputValue.trim()
    if (!name) return
    const existing = data.manualData?.venues ?? []
    if (existing.some((v) => v.name === name)) {
      setVenueInputValue('')
      return
    }
    updateData({
      manualData: {
        ...data.manualData,
        venues: [...existing, {name}],
        venuesCount: existing.length + 1,
      },
    })
    setVenueInputValue('')
  }, [venueInputValue, data.manualData, updateData])

  const handleRemoveVenue = useCallback(
    (idx: number) => {
      const existing = data.manualData?.venues ?? []
      const newVenues = existing.filter((_, i) => i !== idx)
      updateData({
        manualData: {
          ...data.manualData,
          venues: newVenues,
          venuesCount: newVenues.length,
        },
      })
    },
    [data.manualData, updateData]
  )

  const handleVenuesCountChange = useCallback(
    (count: number) => {
      const n = Math.max(0, Math.min(999, count))
      if (n > 0) {
        localStorage.setItem('onboarding_venues_auto_count', String(n))
      } else {
        localStorage.removeItem('onboarding_venues_auto_count')
      }
      updateData({
        manualData: {
          ...data.manualData,
          venuesAutoCount: n,
        },
      })
    },
    [data.manualData, updateData]
  )

  const timings = useMemo(() => data.manualData?.timings ?? [], [data.manualData?.timings])
  const [timingModalOpen, setTimingModalOpen] = useState(false)
  const [timingModalError, setTimingModalError] = useState<string | null>(null)
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  // Mon–Fri preselected, excluding days that already have a timing entry
  const getDefaultTimingDays = useCallback(
    () =>
      DEFAULT_TIMING_DAYS.filter(
        (d) => !timings.some((t: any) => normalizeDay(t.day) === normalizeDay(d))
      ),
    [timings]
  )
  const [newTiming, setNewTiming] = useState({
    selectedDays: DEFAULT_TIMING_DAYS as string[],
    startTime: DEFAULT_TIMING_START,
    endTime: DEFAULT_TIMING_END,
    lectureDuration: DEFAULT_LECTURE_DURATION,
  })

  const toggleDay = useCallback((day: string) => {
    setNewTiming((s) => {
      const sel = s.selectedDays.includes(day)
        ? s.selectedDays.filter((d) => d !== day)
        : [...s.selectedDays, day]
      return {...s, selectedDays: sel}
    })
  }, [])

  // edit mode: only the day being edited stays enabled; add mode: days that already have a timing are disabled
  const isTimingDayDisabled = useCallback(
    (day: string) => {
      if (editingIndex !== null) {
        return normalizeDay(day) !== normalizeDay(timings[editingIndex]?.day)
      }
      return timings.some((t: any) => normalizeDay(t.day) === normalizeDay(day))
    },
    [editingIndex, timings]
  )

  const handleAddTiming = useCallback(() => {
    const existing = data.manualData?.timings ?? []
    const {selectedDays, startTime, endTime, lectureDuration} = newTiming
    if (!startTime || !endTime || selectedDays.length === 0) return

    const startMins = timeToMinutes(startTime)
    const endMins = timeToMinutes(endTime)
    const spanMins = endMins - startMins
    if (spanMins < (lectureDuration ?? 0)) {
      setTimingModalError(
        intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_SPAN_TOO_SHORT'})
      )
      return
    }
    setTimingModalError(null)
    // improved duplicate detection: normalize days and exclude the entry being edited
    const existingDaysExcludingEdited = new Set<string>()
    existing.forEach((t: any, idx: number) => {
      if (editingIndex !== null && idx === editingIndex) return
      existingDaysExcludingEdited.add(normalizeDay(t.day))
    })

    const duplicateDay = selectedDays.find((day) =>
      existingDaysExcludingEdited.has(normalizeDay(day))
    )
    if (duplicateDay) {
      setTimingModalError(
        intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_DUPLICATE_ERROR'})
      )
      return
    }
    const entries = selectedDays.map((day) => ({
      day,
      startTime,
      endTime,
      lectureDuration,
    }))
    if (editingIndex !== null) {
      // support bulk-editing while preserving the edited row's position in the list
      const originalDay = normalizeDay((existing[editingIndex] as any)?.day)
      // keep the entry for the original day first so it lands back on the same row
      const ordered = [
        ...entries.filter((e) => normalizeDay(e.day) === originalDay),
        ...entries.filter((e) => normalizeDay(e.day) !== originalDay),
      ]
      const next = [...existing]
      next.splice(editingIndex, 1, ...ordered)
      updateData({
        manualData: {
          ...data.manualData,
          timings: next,
        },
      })
      setEditingIndex(null)
    } else {
      updateData({
        manualData: {
          ...data.manualData,
          timings: [...existing, ...entries],
        },
      })
    }
    setNewTiming({
      selectedDays: [],
      startTime: DEFAULT_TIMING_START,
      endTime: DEFAULT_TIMING_END,
      lectureDuration: DEFAULT_LECTURE_DURATION,
    })
    setTimingModalOpen(false)
  }, [newTiming, data.manualData, updateData, editingIndex, intl])

  const handleEditTiming = useCallback(
    (idx: number) => {
      const t = timings[idx]
      if (!t) return
      setTimingModalError(null)
      setNewTiming({
        selectedDays: [t.day],
        startTime: t.startTime,
        endTime: t.endTime,
        lectureDuration: t.lectureDuration ?? 0,
      })
      setEditingIndex(idx)
      setTimingModalOpen(true)
    },
    [timings]
  )

  const handleRemoveTiming = useCallback(
    (idx: number) => {
      const existing = data.manualData?.timings ?? []
      updateData({
        manualData: {
          ...data.manualData,
          timings: existing.filter((_, i) => i !== idx),
        },
      })
    },
    [data.manualData, updateData]
  )

  const breakTimings = useMemo(
    () => (data.manualData as any)?.break_timings ?? [],
    [data.manualData]
  )
  const [pendingBreakTimings, setPendingBreakTimings] = useState<
    Array<{day: string; startTime: string; endTime: string}>
  >([])
  // Grid shows only saved breaks; entries buffered via "Add another" stay inside the modal
  // until Save commits them (Cancel discards them).
  const breakTimingsGroupedByDay = useMemo(() => {
    const byDay: Record<string, Array<{startTime: string; endTime: string}>> = {}
    breakTimings.forEach((bt: any) => {
      const day = (bt.day ?? 'MONDAY').toString()
      if (!byDay[day]) byDay[day] = []
      byDay[day].push({startTime: bt.startTime ?? '', endTime: bt.endTime ?? ''})
    })
    return Object.entries(byDay)
  }, [breakTimings])
  const availableTimingDays = useMemo(() => {
    const t = timings ?? []
    return new Set(t.map((x: any) => normalizeDay(x.day)))
  }, [timings])
  // Every weekday already has a timing row: there is nothing left to add (the modal would only
  // report a duplicate-day error), so the "Click to add" footer goes away. Editing a row is
  // still the way to change a day; deleting one brings the button back.
  const allTimingDaysAdded = availableTimingDays.size >= WEEKDAYS.length
  const hasAnyBreakTimings = breakTimings.length > 0
  const [breakTimingModalOpen, setBreakTimingModalOpen] = useState(false)
  const [breakTimingModalError, setBreakTimingModalError] = useState<string | null>(null)
  const [editingBreakDay, setEditingBreakDay] = useState<string | null>(null)
  const [newBreakTiming, setNewBreakTiming] = useState({
    selectedDays: [] as string[],
    startTime: '',
    endTime: '',
  })

  const toggleBreakDay = useCallback((day: string) => {
    setNewBreakTiming((s) => {
      const sel = s.selectedDays.includes(day)
        ? s.selectedDays.filter((d) => d !== day)
        : [...s.selectedDays, day]
      return {...s, selectedDays: sel}
    })
  }, [])

  const handleAddBreakTiming = useCallback(
    (stayOpen?: boolean) => {
      const {selectedDays, startTime, endTime} = newBreakTiming
      if (!startTime || !endTime || selectedDays.length === 0) return
      // validate selected days have timings (off-days cannot have breaks)
      const invalidDay = selectedDays.find((d) => !availableTimingDays.has(normalizeDay(d)))
      if (invalidDay) {
        setBreakTimingModalError(
          intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_DAY_DISABLED'})
        )
        return
      }
      const startMins = timeToMinutes(startTime)
      const endMins = timeToMinutes(endTime)
      if (endMins <= startMins) {
        setBreakTimingModalError(
          intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_END_AFTER_START'})
        )
        return
      }
      // a break must fall completely inside that day's institute timings
      const outsideDay = selectedDays.find((d) => {
        const t: any = (timings ?? []).find((x: any) => normalizeDay(x.day) === normalizeDay(d))
        if (!t?.startTime || !t?.endTime) return false
        return startMins < timeToMinutes(t.startTime) || endMins > timeToMinutes(t.endTime)
      })
      if (outsideDay) {
        const t: any = (timings ?? []).find(
          (x: any) => normalizeDay(x.day) === normalizeDay(outsideDay)
        )
        setBreakTimingModalError(
          intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_OUTSIDE_HOURS'},
            {
              day: intl.formatMessage({id: `DAY.${normalizeDay(outsideDay)}` as any}),
              start: formatTime24to12(t?.startTime ?? ''),
              end: formatTime24to12(t?.endTime ?? ''),
            }
          )
        )
        return
      }
      const existing = (data.manualData as any)?.break_timings ?? []
      // reject breaks that duplicate or overlap one already set for the same day
      // (in edit mode the day's breaks are replaced, so they are not compared against)
      const alreadySet = [...existing, ...pendingBreakTimings].filter((bt: any) =>
        editingBreakDay !== null ? (bt.day ?? '').toString() !== editingBreakDay : true
      )
      const clashDay = selectedDays.find((d) =>
        alreadySet.some(
          (bt: any) =>
            normalizeDay(bt.day) === normalizeDay(d) &&
            startMins < timeToMinutes(bt.endTime) &&
            endMins > timeToMinutes(bt.startTime)
        )
      )
      if (clashDay) {
        setBreakTimingModalError(
          intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_OVERLAP_ERROR'})
        )
        return
      }
      setBreakTimingModalError(null)
      const entries = selectedDays.map((day) => ({day, startTime, endTime}))
      if (editingBreakDay !== null) {
        const dayToReplace = editingBreakDay
        // keep the edited day in place instead of moving its row to the end of the list
        const insertAt = existing.findIndex((bt: any) => (bt.day ?? '').toString() === dayToReplace)
        const filteredExisting = existing.filter(
          (bt: any) => (bt.day ?? '').toString() !== dayToReplace
        )
        setPendingBreakTimings((prev) => prev.filter((bt) => bt.day !== dayToReplace))
        const newEntries = selectedDays.map((d) => ({day: d, startTime, endTime}))
        const nextBreaks = [...filteredExisting]
        nextBreaks.splice(insertAt < 0 ? nextBreaks.length : insertAt, 0, ...newEntries)
        updateData({
          manualData: {
            ...data.manualData,
            break_timings: nextBreaks,
          },
        })
        setEditingBreakDay(null)
        setNewBreakTiming({selectedDays: [], startTime: '', endTime: ''})
        setBreakTimingModalOpen(false)
      } else {
        if (stayOpen) {
          setPendingBreakTimings((prev) => [...prev, ...entries])
          setNewBreakTiming((s) => ({...s, startTime: '', endTime: ''}))
        } else {
          updateData({
            manualData: {
              ...data.manualData,
              break_timings: [...existing, ...pendingBreakTimings, ...entries],
            },
          })
          setPendingBreakTimings([])
          setNewBreakTiming({selectedDays: [], startTime: '', endTime: ''})
          setBreakTimingModalOpen(false)
        }
      }
    },
    [
      newBreakTiming,
      data.manualData,
      editingBreakDay,
      availableTimingDays,
      intl,
      updateData,
      pendingBreakTimings,
      timings,
    ]
  )

  const handleSaveAndCloseBreakModal = useCallback(() => {
    const formValid =
      newBreakTiming.selectedDays.length > 0 && newBreakTiming.startTime && newBreakTiming.endTime
    if (formValid) {
      handleAddBreakTiming(false)
    } else if (breakTimings.length > 0 || pendingBreakTimings.length > 0) {
      const existing = (data.manualData as any)?.break_timings ?? []
      updateData({
        manualData: {
          ...data.manualData,
          break_timings: [...existing, ...pendingBreakTimings],
        },
      })
      setPendingBreakTimings([])
      setNewBreakTiming({selectedDays: [], startTime: '', endTime: ''})
      setBreakTimingModalOpen(false)
      setBreakTimingModalError(null)
    }
  }, [
    newBreakTiming,
    breakTimings.length,
    pendingBreakTimings,
    data.manualData,
    updateData,
    handleAddBreakTiming,
  ])

  const handleEditBreakTiming = useCallback(
    (day: string) => {
      const all = [...breakTimings, ...pendingBreakTimings]
      const firstForDay = all.find((bt: any) => (bt.day ?? '').toString() === day)
      if (!firstForDay) return
      setNewBreakTiming({
        selectedDays: [day],
        startTime: (firstForDay as any).startTime ?? '',
        endTime: (firstForDay as any).endTime ?? '',
      })
      setEditingBreakDay(day)
      setBreakTimingModalError(null)
      setBreakTimingModalOpen(true)
    },
    [breakTimings, pendingBreakTimings]
  )

  const handleRemoveBreakTimingByDay = useCallback(
    (day: string) => {
      const existing = [...((data.manualData as any)?.break_timings ?? [])]
      const filtered = existing.filter((bt: any) => (bt.day ?? '').toString() !== day)
      setPendingBreakTimings((prev) => prev.filter((bt) => bt.day !== day))
      updateData({
        manualData: {
          ...data.manualData,
          break_timings: filtered,
        },
      })
    },
    [data.manualData, updateData]
  )

  const handleKeyDown = (
    e: React.KeyboardEvent,
    type: 'branch' | 'level' | 'venue',
    branchName?: string,
    curriculumName?: string
  ) => {
    const val =
      type === 'branch'
        ? inputValue
        : type === 'venue'
          ? venueInputValue
          : branchName && curriculumName
            ? (levelInputsByCurriculumPerBranch[curriculumName]?.[branchName] ?? '')
            : curriculumName
              ? (levelInputsByCurriculum[curriculumName] ?? '')
              : ''

    if (e.key === 'Enter') {
      e.preventDefault()
      if (type === 'branch') handleAddBranch()
      else if (type === 'venue') handleAddVenue()
      else if (branchName && curriculumName) {
        handleAddLevelToCurriculumBranch(curriculumName, branchName, val, (newVal) =>
          setLevelInputsByCurriculumPerBranch((s) => ({
            ...s,
            [curriculumName]: {...(s[curriculumName] ?? {}), [branchName]: newVal},
          }))
        )
      } else if (curriculumName) {
        handleAddLevelToCurriculum(curriculumName, val, (newVal) =>
          setLevelInputsByCurriculum((s) => ({...s, [curriculumName]: newVal}))
        )
      }
    }
    if (e.key === 'Backspace' && !val) {
      if (type === 'branch') {
        const lastIdx = branches.length - 1
        if (lastIdx >= 0) {
          e.preventDefault()
          handleRemoveBranch(lastIdx)
        }
      } else if (type === 'venue') {
        const lastIdx = venues.length - 1
        if (lastIdx >= 0) {
          e.preventDefault()
          handleRemoveVenue(lastIdx)
        }
      } else if (branchName && curriculumName) {
        const branchLevels =
          data.manualData?.levelsByCurriculum?.[curriculumName]?.levelsPerBranch?.[branchName] ?? []
        const lastIdx = branchLevels.length - 1
        if (lastIdx >= 0) {
          e.preventDefault()
          handleRemoveLevelFromCurriculumBranch(curriculumName, branchName, lastIdx)
        }
      } else if (curriculumName) {
        const currLevels = data.manualData?.levelsByCurriculum?.[curriculumName]?.levels ?? []
        const lastIdx = currLevels.length - 1
        if (lastIdx >= 0) {
          e.preventDefault()
          handleRemoveLevelFromCurriculum(curriculumName, lastIdx)
        }
      }
    }
  }

  const stepLabelByField: Record<Step2SubStep, string> = {
    branches: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCHES'}),
    curriculum: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.CURRICULUM'}),
    levels: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.LEVELS'}),
    duration: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.SESSION'}),
    venues: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES'}),
    timings: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS'}),
    frequency: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.FREQUENCY'}),
    breaks: intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2_REVIEW.BREAKS_TITLE'}),
  }
  const contentStepLabels = stepsToUse.map((s) => stepLabelByField[s])
  const progressLabels =
    extraStepperLabels && extraStepperLabels.length > 0
      ? [...contentStepLabels, ...extraStepperLabels]
      : contentStepLabels

  const durationName = data.manualData?.durationName ?? ''
  const durationStartDate = data.manualData?.durationStartDate ?? ''
  const durationEndDate = data.manualData?.durationEndDate ?? ''
  const minEndDate = useMemo(() => {
    if (!durationStartDate) return undefined
    const d = new Date(durationStartDate + 'T12:00:00')
    d.setDate(d.getDate() + 1)
    return d.toISOString().slice(0, 10)
  }, [durationStartDate])
  const venuesSameForClass = data.manualData?.venuesSameForClass ?? null
  // const defaultSubjectFrequency = data.manualData?.defaultSubjectFrequency ?? 0
  const defaultSubjectFrequency =
    data.manualData?.defaultSubjectFrequency ?? DEFAULT_SUBJECT_FREQUENCY
  const defaultFrequencyPer = data.manualData?.defaultFrequencyPer ?? 'week'

  const isVenuesWithList = activeField === 'venues' && venuesSameForClass === false

  return (
    <div
      className={`d-flex flex-column step2-basic-setup${
        activeField === 'curriculum' ? ' step2-curriculum-active' : ''
      }${activeField === 'levels' ? ' step2-levels-active' : ''}${
        activeField === 'venues' ? ' step2-venues-active' : ''
      }${activeField === 'timings' ? ' step2-timings-active' : ''}${
        activeField === 'frequency' ? ' step2-frequency-active' : ''
      }`}
      style={{paddingTop: '0.5rem'}}
    >
      {/* Horizontal progress bar - same size/alignment as Step4 */}
      {!hideStepper && (
        <div className='step2-stepper-wrapper mb-3'>
          <div
            className='step2-horizontal-stepper'
            style={{'--step-count': progressLabels.length} as React.CSSProperties}
          >
            <div
              className='step2-h-track'
              style={{
                background:
                  activeSubStep === 0
                    ? `linear-gradient(90deg, ${isDark ? '#2b3a52' : '#e4e6ef'} 0%, ${isDark ? '#2b3a52' : '#e4e6ef'} 100%)`
                    : `linear-gradient(90deg, var(--primary-color, #056EE9) 0%, var(--primary-color, #056EE9) ${(activeSubStep / (progressLabels.length - 1 || 1)) * 100}%, ${isDark ? '#2b3a52' : '#e4e6ef'} ${(activeSubStep / (progressLabels.length - 1 || 1)) * 100}%)`,
                height: 4,
                borderRadius: 2,
              }}
            />
            {progressLabels.map((_, idx) => {
              const isActive = idx === activeSubStep
              // Progress (fill + ticks) follows the CURRENT position so it walks back with
              // the Back button; clickability still honors the furthest step reached.
              const isCompleted = idx < activeSubStep
              const isClickable = idx <= maxSubStepReached && idx < stepsToUse.length
              return (
                <div
                  key={idx}
                  className={`step2-h-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isActive && completing ? 'completing' : ''} ${isClickable ? 'step2-h-step--clickable' : ''}`}
                  style={{cursor: isClickable ? 'pointer' : 'default'}}
                  onClick={() => {
                    if (isClickable && idx !== activeSubStep) {
                      setActiveSubStep(idx)
                    }
                  }}
                  onKeyDown={(e) => {
                    if (
                      isClickable &&
                      idx !== activeSubStep &&
                      (e.key === 'Enter' || e.key === ' ')
                    ) {
                      e.preventDefault()
                      setActiveSubStep(idx)
                    }
                  }}
                  role='button'
                  tabIndex={isClickable ? 0 : -1}
                >
                  <div
                    className='step2-h-step-icon'
                    style={{
                      backgroundColor:
                        isActive || isCompleted
                          ? 'var(--primary-color, #056EE9)'
                          : isDark
                            ? '#2b2b40'
                            : '#e4e6ef',
                      color: isActive || isCompleted ? '#ffffff' : isDark ? '#92929f' : '#7e8299',
                      borderColor:
                        isActive || isCompleted
                          ? 'var(--primary-color, #056EE9)'
                          : isDark
                            ? '#2b2b40'
                            : '#e4e6ef',
                      width: 40,
                      height: 40,
                      fontSize: '1.2rem',
                      boxShadow: isActive ? '0 0 15px rgba(5, 110, 233, 0.4)' : 'none',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    {idx < activeSubStep ? (
                      <span className='step2-tick-icon'>✓</span>
                    ) : idx === activeSubStep && completing ? (
                      <span className='step2-tick-icon'>✓</span>
                    ) : (
                      toRomanNumeral(idx + 1)
                    )}
                  </div>
                  <div
                    className='step2-h-step-label'
                    style={{
                      color: isActive
                        ? 'var(--primary-color, #056EE9)'
                        : isCompleted
                          ? isDark
                            ? '#92929f'
                            : '#7E8299'
                          : '#7e8299',
                      fontWeight: isActive ? 700 : 500,
                    }}
                  >
                    {progressLabels[idx]}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Sub-step content - scrollable when venues + second option or timings step */}
      <div
        className={
          isVenuesWithList || activeField === 'timings' ? 'step2-substep-scroll-wrap' : undefined
        }
      >
        {activeField === 'branches' && (
          <div
            key={activeSubStep}
            className={`step2-substep-content step2-branches-content${completing ? ' step2-substep-exit' : ''}`}
          >
            <div className='d-flex flex-column' style={{gap: '1.5rem', paddingTop: '2.5rem'}}>
              <div className='text-center px-4'>
                <p
                  style={{
                    color: isDark ? '#ffffff' : '#181C32',
                    fontSize: '1.35rem',
                    fontWeight: 500,
                    lineHeight: 1.7,
                    margin: 0,
                  }}
                >
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCHES_INSTRUCTION'})}
                </p>
              </div>
              <p
                className='text-center mb-0'
                style={{
                  color: isDark ? '#92929f' : '#7E8299',
                  fontSize: '0.98rem',
                  lineHeight: 1.6,
                }}
              >
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCHES_PLACEHOLDER'})}
              </p>

              <div
                className='step2-branch-input'
                onClick={() => document.getElementById('step2-branch-input')?.focus()}
                style={{
                  // Dark theme keeps the original bluish gradient; light theme is plain white.
                  ...(isDark
                    ? {
                        background:
                          'linear-gradient(180deg, rgba(11, 74, 209, 0.15) 0%, rgba(153, 153, 153, 0.15) 100%)',
                      }
                    : {}),
                  boxShadow: '0px 2px 2px 0px #0000001A',
                }}
              >
                <div className='d-flex flex-wrap align-items-center gap-2 p-3'>
                  {branches.map((name, idx) => (
                    <span key={idx} className='step2-branch-tag'>
                      {name}
                      {/* Previous: {name !== 'Main Branch' && ( */}
                      {(branches.length === 1 || name !== DEFAULT_BRANCH_NAME) && (
                        <button
                          type='button'
                          className='step2-branch-tag-remove'
                          onClick={(e) => {
                            e.stopPropagation()
                            handleRemoveBranch(idx)
                          }}
                          aria-label='Remove'
                        >
                          <TagRemoveIcon />
                        </button>
                      )}
                    </span>
                  ))}
                  <input
                    id='step2-branch-input'
                    type='text'
                    value={inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value)
                      if (branchInlineError) setBranchInlineError(null)
                    }}
                    onKeyDown={(e) => handleKeyDown(e, 'branch')}
                    placeholder={intl.formatMessage({
                      id: 'ONBOARDING_WIZARD.STEP2.BRANCHES_INPUT_PLACEHOLDER',
                    })}
                    className='step2-branch-input-field'
                    style={{
                      border: 'none',
                      outline: 'none',
                      background: 'transparent',
                      flex: 1,
                      minWidth: '120px',
                      color: isDark ? '#ffffff' : '#181C32',
                    }}
                  />
                </div>
              </div>
              {branchInlineError && (
                <p
                  className='mt-2 mb-0 text-center'
                  style={{color: '#dc3545', fontSize: '0.875rem', fontWeight: 600}}
                >
                  {branchInlineError}
                </p>
              )}
              <p
                className='text-center mb-0'
                style={{
                  color: isDark ? '#92929f' : '#7E8299',
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                }}
              >
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.BRANCHES_SINGLE_CAMPUS_NOTE'})}
              </p>
            </div>
          </div>
        )}

        {activeField === 'curriculum' && (
          <div
            key={activeSubStep}
            className={`step2-substep-content step2-curriculum-content${completing ? ' step2-substep-exit' : ''}`}
          >
            <div className='d-flex flex-column' style={{gap: '1.5rem', paddingTop: '2.5rem'}}>
              <div className='text-center px-4'>
                <p
                  style={{
                    color: isDark ? '#ffffff' : '#181C32',
                    fontSize: '1.35rem',
                    fontWeight: 500,
                    lineHeight: 1.7,
                    margin: 0,
                  }}
                >
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.CURRICULUM.TITLE'})}
                </p>
                <p
                  className='mt-2'
                  style={{
                    color: isDark ? '#92929f' : '#7E8299',
                    fontSize: '1rem',
                    lineHeight: 1.6,
                  }}
                >
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.CURRICULUM.SUBTITLE'})}
                </p>
              </div>
              <div className='curriculum-selection-container px-5'>
                <div
                  className='step2-curriculum-picker mx-auto position-relative'
                  style={{maxWidth: '750px', width: '100%'}}
                  ref={dropdownRef}
                >
                  <div className='step2-curriculum-control' onClick={() => setIsDropdownOpen(true)}>
                    <div className='step2-curriculum-values'>
                      {selectedCurriculums.map((curriculum, idx) => (
                        <span key={idx} className='step2-curriculum-chip'>
                          <span className='step2-curriculum-chip-label'>{curriculum}</span>
                          <button
                            type='button'
                            className='step2-curriculum-chip-remove'
                            onClick={(e) => {
                              e.stopPropagation()
                              handleToggleCurriculum(curriculum)
                            }}
                            aria-label={`Remove ${curriculum}`}
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                      {otherEditing && (
                        <span className='step2-curriculum-chip'>
                          <input
                            ref={otherInputRef}
                            type='text'
                            className='step2-curriculum-search-input'
                            placeholder={intl.formatMessage({
                              id: 'ONBOARDING_WIZARD.CURRICULUM.OTHER_PLACEHOLDER',
                            })}
                            value={otherValue}
                            onChange={(e) => setOtherValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                handleConfirmOther()
                              } else if (e.key === 'Escape') {
                                setOtherEditing(false)
                                setOtherValue('')
                              }
                            }}
                            onBlur={() => {
                              if ((otherValue ?? '').trim()) handleConfirmOther()
                              else setOtherEditing(false)
                            }}
                          />
                          <button
                            type='button'
                            className='step2-curriculum-chip-remove'
                            onClick={(e) => {
                              e.stopPropagation()
                              setOtherEditing(false)
                              setOtherValue('')
                            }}
                            aria-label='Cancel'
                          >
                            <X size={12} />
                          </button>
                        </span>
                      )}
                      <div className='step2-curriculum-search-slot'>
                        <input
                          type='text'
                          className='step2-curriculum-search-input'
                          placeholder={
                            selectedCurriculums.length === 0
                              ? intl.formatMessage({
                                  id: 'ONBOARDING_WIZARD.CURRICULUM.SEARCH_PLACEHOLDER',
                                })
                              : ''
                          }
                          value={curriculumSearch}
                          onChange={(e) => {
                            setCurriculumSearch(e.target.value)
                            setIsDropdownOpen(true)
                          }}
                          onFocus={() => setIsDropdownOpen(true)}
                          onKeyDown={(e) => {
                            // Mirror the branches/levels behavior: backspace on an empty
                            // input removes the last selected curriculum chip.
                            if (e.key === 'Backspace' && !curriculumSearch && !otherEditing) {
                              const last = selectedCurriculums[selectedCurriculums.length - 1]
                              if (last) {
                                e.preventDefault()
                                handleToggleCurriculum(last)
                              }
                            }
                          }}
                        />
                      </div>
                    </div>
                    <span className='step2-curriculum-search-icon' aria-hidden='true'>
                      <Search size={16} />
                    </span>
                  </div>

                  {isDropdownOpen && (
                    <div className='step2-curriculum-dropdown'>
                      {isLoadingSystems ? (
                        <div className='d-flex align-items-center justify-content-center p-10'>
                          <div className='spinner-border text-primary' role='status'>
                            <span className='visually-hidden'>Loading...</span>
                          </div>
                        </div>
                      ) : (
                        <div className='step2-curriculum-dropdown-list'>
                          {filteredSystems.map((system, idx) => {
                            const isSelected = selectedCurriculums.includes(system.system)

                            return (
                              <button
                                key={`${system.system}-${idx}`}
                                type='button'
                                className={`step2-curriculum-option${isSelected ? ' is-selected' : ''}`}
                                onClick={() => handleToggleCurriculum(system.system)}
                              >
                                <span className='step2-curriculum-option-label'>
                                  {system.system}
                                </span>
                                <span className='step2-curriculum-option-action' aria-hidden='true'>
                                  {isSelected ? <Check size={14} /> : <Plus size={14} />}
                                </span>
                              </button>
                            )
                          })}
                          {filteredSystems.length === 0 && (
                            <div className='step2-curriculum-empty-state'>
                              {curriculumSearch.trim()
                                ? 'No Curriculum Options Found'
                                : intl.formatMessage({
                                    id: 'ONBOARDING_WIZARD.CURRICULUM.SEARCH_PLACEHOLDER',
                                  })}
                            </div>
                          )}
                          <button
                            type='button'
                            className='step2-curriculum-option step2-curriculum-option-other'
                            onClick={() => {
                              setOtherEditing(true)
                              setIsDropdownOpen(false)
                              setOtherValue('')
                            }}
                          >
                            <span className='step2-curriculum-option-label'>
                              {intl.formatMessage({id: 'ONBOARDING_WIZARD.CURRICULUM.OTHER'})}
                            </span>
                            <span className='step2-curriculum-option-action' aria-hidden='true'>
                              <Plus size={14} />
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <p
                className='text-center mt-2 mb-0 px-4'
                style={{
                  color: isDark ? '#92929f' : '#7E8299',
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                }}
              >
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.CURRICULUM.PREFILL_HINT'})}
              </p>
              {/* Previous: {intl.formatMessage({id: 'ONBOARDING_WIZARD.CURRICULUM.REGION_HINT'})} */}
            </div>
          </div>
        )}

        {activeField === 'levels' && (
          <div
            key={activeSubStep}
            className={`step2-substep-content step2-levels-content${completing ? ' step2-substep-exit' : ''}`}
            style={{paddingBottom: '2rem'}}
          >
            {selectedCurriculums.map((currName, currIdx) => {
              const curriculumData = data.manualData?.levelsByCurriculum?.[currName]
              // With a single curriculum the header is just a heading, not a collapsible
              // group, so it stays expanded and shows no drop-down arrow.
              const isSingleCurriculum = selectedCurriculums.length === 1
              const isExpanded =
                isSingleCurriculum ||
                expandedCurriculum.size === 0 ||
                expandedCurriculum.has(currName)

              const toggleExpanded = () => {
                setExpandedCurriculum((prev) => {
                  const base = prev.size === 0 ? new Set(selectedCurriculums) : new Set(prev)
                  if (isExpanded) base.delete(currName)
                  else base.add(currName)
                  return base
                })
              }

              const levelsSameInBranches =
                branches.length <= 1 ? true : (curriculumData?.levelsSameAcrossBranches ?? true)
              const currLevels = curriculumData?.levels ?? []
              const currLevelsPerBranch = curriculumData?.levelsPerBranch ?? {}

              return (
                <div
                  key={currName}
                  className={`step2-curriculum-group mb-4${isExpanded ? ' is-expanded' : ''}`}
                  style={{
                    borderBottom:
                      currIdx < selectedCurriculums.length - 1
                        ? `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e4e6ef'}`
                        : 'none',
                    paddingBottom: '1rem',
                  }}
                >
                  <div
                    className={`d-flex align-items-center py-3${isSingleCurriculum ? '' : ' cursor-pointer'}`}
                    onClick={isSingleCurriculum ? undefined : toggleExpanded}
                    style={{gap: '12px'}}
                  >
                    {!isSingleCurriculum &&
                      (isExpanded ? (
                        <ChevronUp size={20} color='#056EE9' />
                      ) : (
                        <ChevronDown size={20} color={isDark ? '#92929f' : '#6c757d'} />
                      ))}
                    <span
                      style={{
                        fontFamily: 'Inter',
                        fontSize: '1.2rem',
                        fontWeight: 600,
                        color: isExpanded ? '#056EE9' : isDark ? '#ffffff' : '#181C32',
                      }}
                    >
                      {currName}
                    </span>
                  </div>

                  {isExpanded && (
                    <div className='ms-lg-8 mt-2'>
                      {branches.length > 1 && (
                        <>
                          <p
                            className='step2-levels-question'
                            style={{
                              fontFamily: 'Inter',
                              fontSize: '1.1rem',
                              fontWeight: 500,
                              color: isDark ? '#ffffff' : '#181C32',
                              marginBottom: '0.4rem',
                            }}
                          >
                            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.LEVELS_QUESTION'})}
                          </p>
                          <p
                            className='step2-levels-helper'
                            style={{
                              fontFamily: 'Inter',
                              fontSize: '0.85rem',
                              color: isDark ? '#92929f' : '#6c757d',
                              marginBottom: '1.2rem',
                            }}
                          >
                            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.LEVELS_HELPER'})}
                          </p>

                          <div className='step2-radio-group mb-4'>
                            <label
                              className={`step2-radio-option ${levelsSameInBranches === true ? 'active' : ''}`}
                            >
                              <input
                                type='radio'
                                name={`levelsSameAcrossBranches-${currName}`}
                                checked={levelsSameInBranches === true}
                                onChange={() =>
                                  updateData({
                                    manualData: {
                                      ...data.manualData,
                                      levelsByCurriculum: {
                                        ...data.manualData?.levelsByCurriculum,
                                        [currName]: {
                                          levels: currLevels,
                                          levelsSameAcrossBranches: true,
                                          levelsPerBranch: curriculumData?.levelsPerBranch ?? {},
                                        },
                                      },
                                    },
                                  })
                                }
                              />
                              <span className='step2-radio-dot' />
                              <span className='step2-radio-text'>
                                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.LEVELS_SAME'})}
                              </span>
                            </label>
                            <label
                              className={`step2-radio-option ${levelsSameInBranches === false ? 'active' : ''}`}
                            >
                              <input
                                type='radio'
                                name={`levelsSameAcrossBranches-${currName}`}
                                checked={levelsSameInBranches === false}
                                onChange={() => {
                                  const levelNames = currLevels.map((l) => l.name)
                                  const branchList = data.manualData?.branches ?? []
                                  const newLevelsPerBranch: Record<string, string[]> = {}
                                  for (const b of branchList) {
                                    newLevelsPerBranch[b] = [...levelNames]
                                  }
                                  updateData({
                                    manualData: {
                                      ...data.manualData,
                                      levelsByCurriculum: {
                                        ...data.manualData?.levelsByCurriculum,
                                        [currName]: {
                                          levels: currLevels,
                                          levelsSameAcrossBranches: false,
                                          levelsPerBranch: newLevelsPerBranch,
                                        },
                                      },
                                    },
                                  })
                                }}
                              />
                              <span className='step2-radio-dot' />
                              <span className='step2-radio-text'>
                                {intl.formatMessage({
                                  id: 'ONBOARDING_WIZARD.STEP2.LEVELS_DIFFERENT',
                                })}
                              </span>
                            </label>
                          </div>
                        </>
                      )}

                      {levelsSameInBranches === true && (
                        <div className='mt-4'>
                          <p
                            className='step2-levels-label'
                            style={{
                              fontFamily: 'Inter',
                              fontSize: '0.9rem',
                              color: isDark ? '#92929f' : '#6c757d',
                              marginBottom: '0.75rem',
                            }}
                          >
                            Type a level name and press ENTER
                          </p>

                          <div
                            className='step2-branch-input'
                            onClick={() =>
                              document.getElementById(`step2-level-input-${currIdx}`)?.focus()
                            }
                          >
                            <div className='d-flex flex-wrap align-items-center gap-2 p-3'>
                              {currLevels.map((level, idx) => (
                                <span key={idx} className='step2-branch-tag'>
                                  {level.name}
                                  <button
                                    type='button'
                                    className='step2-branch-tag-remove'
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleRemoveLevelFromCurriculum(currName, idx)
                                    }}
                                    aria-label='Remove'
                                  >
                                    <TagRemoveIcon />
                                  </button>
                                </span>
                              ))}
                              <input
                                id={`step2-level-input-${currIdx}`}
                                type='text'
                                value={levelInputsByCurriculum[currName] ?? ''}
                                onChange={(e) =>
                                  setLevelInputsByCurriculum((prev) => ({
                                    ...prev,
                                    [currName]: e.target.value,
                                  }))
                                }
                                onKeyDown={(e) => handleKeyDown(e, 'level', undefined, currName)}
                                placeholder=''
                                className='step2-branch-input-field'
                                style={{
                                  border: 'none',
                                  outline: 'none',
                                  background: 'transparent',
                                  flex: 1,
                                  minWidth: '120px',
                                  color: isDark ? '#ffffff' : '#181C32',
                                }}
                              />
                            </div>
                          </div>
                          <p
                            className='mt-2'
                            style={{
                              fontSize: '0.85rem',
                              color: isDark ? '#6c757d' : '#92929f',
                              textAlign: 'center',
                            }}
                          >
                            Data is pre-filled based on your curriculum options. You can modify it
                            if needed.
                          </p>
                        </div>
                      )}

                      {levelsSameInBranches === false && (
                        <div className='step2-levels-per-branch mt-4'>
                          <p
                            className='step2-levels-label mb-3'
                            style={{
                              fontFamily: 'Inter',
                              fontSize: '0.9rem',
                              color: isDark ? '#92929f' : '#6c757d',
                            }}
                          >
                            Type a level name and press ENTER
                          </p>
                          {branches.map((branchName) => (
                            <div
                              key={branchName}
                              className='d-flex flex-column'
                              style={{gap: '0.5rem'}}
                            >
                              <div className='step2-branch-level-row'>
                                <div
                                  className='step2-branch-level-input'
                                  onClick={() =>
                                    document
                                      .getElementById(
                                        `step2-level-input-${currName.replace(/\s/g, '-')}-${branchName.replace(/\s/g, '-')}`
                                      )
                                      ?.focus()
                                  }
                                >
                                  <div className='d-flex flex-wrap align-items-center gap-2 p-3'>
                                    {(currLevelsPerBranch[branchName] ?? []).map(
                                      (levelName, idx) => (
                                        <span
                                          key={idx}
                                          className='step2-branch-tag step2-level-tag-row'
                                        >
                                          {levelName}
                                          <button
                                            type='button'
                                            className='step2-branch-tag-remove'
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              handleRemoveLevelFromCurriculumBranch(
                                                currName,
                                                branchName,
                                                idx
                                              )
                                            }}
                                            aria-label='Remove'
                                          >
                                            <svg
                                              width='8'
                                              height='8'
                                              viewBox='0 0 8 8'
                                              fill='none'
                                              aria-hidden='true'
                                            >
                                              <path
                                                d='M1 1L7 7M7 1L1 7'
                                                stroke='currentColor'
                                                strokeWidth='1.5'
                                                strokeLinecap='round'
                                              />
                                            </svg>
                                          </button>
                                        </span>
                                      )
                                    )}
                                    <input
                                      id={`step2-level-input-${currName.replace(/\s/g, '-')}-${branchName.replace(/\s/g, '-')}`}
                                      type='text'
                                      value={
                                        levelInputsByCurriculumPerBranch[currName]?.[branchName] ??
                                        ''
                                      }
                                      onChange={(e) =>
                                        setLevelInputsByCurriculumPerBranch((s) => ({
                                          ...s,
                                          [currName]: {
                                            ...(s[currName] ?? {}),
                                            [branchName]: e.target.value,
                                          },
                                        }))
                                      }
                                      onKeyDown={(e) =>
                                        handleKeyDown(e, 'level', branchName, currName)
                                      }
                                      placeholder=''
                                      className='step2-branch-input-field'
                                      style={{
                                        border: 'none',
                                        outline: 'none',
                                        background: 'transparent',
                                        flex: 1,
                                        minWidth: '100px',
                                        color: isDark ? '#ffffff' : '#181C32',
                                      }}
                                    />
                                  </div>
                                </div>
                                <div className='step2-branch-name-badge'>{branchName}</div>
                              </div>
                            </div>
                          ))}
                          <p
                            className='mt-3'
                            style={{
                              fontSize: '0.85rem',
                              color: isDark ? '#6c757d' : '#92929f',
                              textAlign: 'center',
                            }}
                          >
                            Data is pre-filled based on your curriculum options. You can modify it
                            if needed.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {activeField === 'duration' && (
          <div
            key={activeSubStep}
            className={`step2-substep-content step2-duration-content${completing ? ' step2-substep-exit' : ''}`}
          >
            <p
              className='step2-levels-question'
              style={{
                fontFamily: 'Inter',
                fontSize: '1.25rem',
                fontWeight: 600,
                color: isDark ? '#ffffff' : '#181C32',
                marginBottom: '0.5rem',
                paddingTop: '2rem',
              }}
            >
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.SESSION_QUESTION'})}
            </p>
            <p
              className='step2-levels-helper'
              style={{
                fontFamily: 'Inter',
                fontSize: '0.9rem',
                color: isDark ? '#92929f' : '#6c757d',
                marginBottom: '2.5rem',
              }}
            >
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.SESSION_HELPER'})}
            </p>

            <div className='step2-duration-inputs mb-14'>
              <div className='step2-duration-field'>
                <label className='step2-duration-label' htmlFor='step2-session-name'>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.SESSION_NAME_LABEL'})}
                </label>
                <input
                  id='step2-session-name'
                  type='text'
                  className='step2-duration-input'
                  placeholder={intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP2.DURATION_NAME_PLACEHOLDER',
                  })}
                  value={durationName}
                  onChange={(e) =>
                    updateData({
                      manualData: {...data.manualData, durationName: e.target.value},
                    })
                  }
                  style={{
                    color: isDark ? '#ffffff' : '#181C32',
                  }}
                />
              </div>
              <div
                className='step2-duration-field step2-duration-field--date'
                style={{position: 'relative'}}
              >
                <label className='step2-duration-label' htmlFor='step2-session-start-date'>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.SESSION_START_DATE_LABEL'})}
                </label>
                <WizardDatePicker
                  id='step2-session-start-date'
                  className='step2-duration-input step2-duration-date'
                  min={SESSION_DATE_MIN}
                  max={SESSION_DATE_MAX}
                  value={durationStartDate}
                  style={{color: isDark ? '#ffffff' : '#181C32'}}
                  ariaLabel={intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP2.SESSION_START_DATE_LABEL',
                  })}
                  onChange={(start) => {
                    const end = data.manualData?.durationEndDate ?? ''
                    updateData({
                      manualData: {
                        ...data.manualData,
                        durationStartDate: start,
                        durationEndDate: end && start && end <= start ? '' : end,
                      },
                    })
                  }}
                />
                {durationStartDate && durationEndDate && durationEndDate <= durationStartDate && (
                  <p
                    className='step2-duration-error'
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      marginTop: '0.5rem',
                      marginBottom: 0,
                      whiteSpace: 'nowrap',
                      fontFamily: 'Inter',
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      color: '#f1416c',
                      lineHeight: 1.5,
                    }}
                  >
                    {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.SESSION_END_AFTER_START'})}
                  </p>
                )}
              </div>
              <div className='step2-duration-field step2-duration-field--date'>
                <label className='step2-duration-label' htmlFor='step2-session-end-date'>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.SESSION_END_DATE_LABEL'})}
                </label>
                <WizardDatePicker
                  id='step2-session-end-date'
                  className='step2-duration-input step2-duration-date'
                  placeholder={intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP2.DURATION_END_PLACEHOLDER',
                  })}
                  min={minEndDate ?? SESSION_DATE_MIN}
                  max={SESSION_DATE_MAX}
                  value={durationEndDate}
                  style={{color: isDark ? '#ffffff' : '#181C32'}}
                  ariaLabel={intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP2.SESSION_END_DATE_LABEL',
                  })}
                  onChange={(end) =>
                    updateData({
                      manualData: {...data.manualData, durationEndDate: end},
                    })
                  }
                />
              </div>
            </div>

            <p
              className='step2-duration-info'
              style={{
                fontFamily: 'Inter',
                fontSize: '0.9rem',
                color: isDark ? '#92929f' : '#6c757d',
                lineHeight: 1.5,
              }}
            >
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.SESSION_INFO'})}
            </p>
          </div>
        )}

        {activeField === 'venues' && (
          <div
            key={activeSubStep}
            className={`step2-substep-content step2-venues-content${completing ? ' step2-substep-exit' : ''}`}
          >
            <div className='d-flex flex-column' style={{gap: '1.5rem', paddingTop: '1.5rem'}}>
              <div className='d-flex flex-column align-items-center' style={{gap: '0.5rem'}}>
                <p
                  className='text-center px-4 mb-0'
                  style={{
                    color: isDark ? '#ffffff' : '#181C32',
                    fontSize: '1.35rem',
                    fontWeight: 500,
                    lineHeight: 1.7,
                  }}
                >
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_QUESTION'})}
                </p>
                {/* <p
                  className='text-center px-4 mb-0'
                  style={{
                    color: isDark ? '#92929f' : '#7E8299',
                    fontSize: '0.98rem',
                    lineHeight: 1.6,
                  }}
                >
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_HELPER'})}
                </p> */}
              </div>

              <div className='d-flex gap-4'>
                <div
                  style={{
                    cursor: 'pointer',
                    borderRadius: 14,
                    padding: '2.5rem 2rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'all 0.25s ease',
                    border: '2px solid',
                    flex: 1,
                    borderColor:
                      venuesSameForClass === true ? '#056EE9' : isDark ? '#2b2b40' : '#dee2e6',
                    backgroundColor:
                      venuesSameForClass === true
                        ? isDark
                          ? 'rgba(0, 149, 232, 0.08)'
                          : 'rgba(0, 149, 232, 0.04)'
                        : isDark
                          ? 'rgba(255,255,255,0.04)'
                          : '#ffffff',
                    boxShadow:
                      venuesSameForClass === true
                        ? '0 0 0 1px rgba(0,149,232,0.3)'
                        : '0 2px 8px rgba(0,0,0,0.06)',
                  }}
                  onClick={() =>
                    updateData({
                      manualData: {...data.manualData, venuesSameForClass: true},
                    })
                  }
                >
                  <div className='mb-4' style={{lineHeight: 3}}>
                    <i
                      className='fas fa-chalkboard-teacher'
                      style={{
                        fontSize: '2.7rem',
                        color: isDark ? '#d1d3e0' : '#5E6278',
                      }}
                    />
                  </div>
                  <h6
                    className='fw-bold mb-2'
                    style={{
                      color: isDark ? '#ffffff' : '#181C32',
                      fontSize: '1.2rem',
                    }}
                  >
                    {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_SAME'})}
                  </h6>
                  <p
                    className='mb-0'
                    style={{
                      color: isDark ? '#92929f' : '#7E8299',
                      fontSize: '0.98rem',
                      lineHeight: 1.6,
                    }}
                  >
                    {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_SAME_DESC'})}
                  </p>
                </div>

                <div
                  style={{
                    cursor: 'pointer',
                    borderRadius: 14,
                    padding: '2.5rem 2rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'all 0.25s ease',
                    border: '2px solid',
                    flex: 1,
                    borderColor:
                      venuesSameForClass === false ? '#056EE9' : isDark ? '#2b2b40' : '#dee2e6',
                    backgroundColor:
                      venuesSameForClass === false
                        ? isDark
                          ? 'rgba(0, 149, 232, 0.08)'
                          : 'rgba(0, 149, 232, 0.04)'
                        : isDark
                          ? 'rgba(255,255,255,0.04)'
                          : '#ffffff',
                    boxShadow:
                      venuesSameForClass === false
                        ? '0 0 0 1px rgba(0,149,232,0.3)'
                        : '0 2px 8px rgba(0,0,0,0.06)',
                  }}
                  onClick={() =>
                    updateData({
                      manualData: {...data.manualData, venuesSameForClass: false},
                    })
                  }
                >
                  <div className='mb-4' style={{lineHeight: 3}}>
                    <i
                      className='fas fa-book-open'
                      style={{
                        fontSize: '2.7rem',
                        color: isDark ? '#d1d3e0' : '#5E6278',
                      }}
                    />
                  </div>
                  <h6
                    className='fw-bold mb-2'
                    style={{
                      color: isDark ? '#ffffff' : '#181C32',
                      fontSize: '1.2rem',
                    }}
                  >
                    {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_DIFFERENT'})}
                  </h6>
                  <p
                    className='mb-0'
                    style={{
                      color: isDark ? '#92929f' : '#7E8299',
                      fontSize: '0.98rem',
                      lineHeight: 1.6,
                    }}
                  >
                    {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_DIFFERENT_DESC'})}
                  </p>
                </div>
              </div>

              <div className='text-center mt-2'>
                <p
                  className='mb-1'
                  style={{color: isDark ? '#92929f' : '#7E8299', fontSize: '0.875rem'}}
                >
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_APPLIED_INFO'})}
                </p>
                <p
                  className='mb-0'
                  style={{color: isDark ? '#92929f' : '#7E8299', fontSize: '0.875rem'}}
                >
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_APPLIED_CONSTRAINTS'})}
                </p>
              </div>

              {venuesSameForClass === false && (
                <div
                  className='d-flex flex-column step2-venues-list-section'
                  style={{gap: '1.5rem'}}
                >
                  <h6
                    className='mb-0 text-center'
                    style={{
                      color: isDark ? '#ffffff' : '#181C32',
                      fontSize: '1.25rem',
                      fontWeight: 600,
                    }}
                  >
                    {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_LIST_TITLE'})}
                  </h6>

                  <div
                    className={`d-flex flex-column${venueInputMode === 'manual' ? ' step2-venue-count-wrapper step2-venue-count-wrapper-disabled' : ''}`}
                    style={{gap: '0.5rem'}}
                  >
                    <p
                      className='mb-0'
                      style={{
                        color: isDark ? '#e4e6ef' : '#7E8299',
                        fontSize: '0.9rem',
                      }}
                    >
                      {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_AUTO_HINT'})}
                    </p>
                    <input
                      type='number'
                      min={0}
                      max={999}
                      value={venueInputMode === 'manual' ? '' : venuesAutoCount || ''}
                      disabled={venueInputMode === 'manual'}
                      style={{
                        width: 180,
                        maxWidth: 180,
                        padding: '0.5rem 0.6rem',
                        background:
                          venueInputMode === 'manual'
                            ? isDark
                              ? 'rgba(255, 255, 255, 0.06)'
                              : 'rgba(126, 130, 153, 0.12)'
                            : isDark
                              ? '#1e1e2e'
                              : '#ffffff',
                        border:
                          venueInputMode === 'manual'
                            ? isDark
                              ? '1px solid rgba(255, 255, 255, 0.15)'
                              : '1px solid rgba(126, 130, 153, 0.4)'
                            : '1px solid #056EE9',
                        color:
                          venueInputMode === 'manual'
                            ? isDark
                              ? '#92929f'
                              : '#7e8299'
                            : isDark
                              ? '#ffffff'
                              : '#181C32',
                        borderRadius: 12,
                        outline: 'none',
                        opacity: venueInputMode === 'manual' ? 0.8 : 1,
                      }}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10)
                        handleVenuesCountChange(isNaN(v) ? 0 : v)
                      }}
                      placeholder={intl.formatMessage({
                        id: 'ONBOARDING_WIZARD.STEP2.VENUES_COUNT_PLACEHOLDER',
                      })}
                      className={`step2-venue-count-input${venueInputMode === 'manual' ? ' step2-venue-count-input-disabled' : ''}`}
                    />
                  </div>

                  <div
                    className='d-flex align-items-center gap-3'
                    style={{color: isDark ? '#e4e6ef' : '#7E8299', fontSize: '1.65rem'}}
                  >
                    <div style={{flex: 1, height: 1, background: isDark ? '#3f4254' : '#dee2e6'}} />
                    <span>OR</span>
                    <div style={{flex: 1, height: 1, background: isDark ? '#3f4254' : '#dee2e6'}} />
                  </div>

                  <div className='d-flex flex-column' style={{gap: '0.5rem'}}>
                    <p
                      className='mb-0'
                      style={{
                        color: isDark ? '#e4e6ef' : '#7E8299',
                        fontSize: '0.9rem',
                      }}
                    >
                      {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.VENUES_MANUAL_HINT'})}
                    </p>
                    <div
                      className={`step2-venue-input${venueInputMode === 'auto' ? ' step2-venue-input-disabled' : ''}`}
                      onClick={() =>
                        venueInputMode !== 'auto' &&
                        document.getElementById('step2-venue-input')?.focus()
                      }
                    >
                      <div className='d-flex flex-wrap align-items-center gap-2 p-3'>
                        {venues.map((v, idx) => (
                          <span key={idx} className='step2-venue-tag'>
                            {v.name}
                            <button
                              type='button'
                              className='step2-venue-tag-remove'
                              onClick={(e) => {
                                e.stopPropagation()
                                handleRemoveVenue(idx)
                              }}
                              aria-label='Remove'
                            >
                              <TagRemoveIcon />
                            </button>
                          </span>
                        ))}
                        <input
                          id='step2-venue-input'
                          type='text'
                          value={venueInputValue}
                          onChange={(e) => setVenueInputValue(e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, 'venue')}
                          placeholder=''
                          className='step2-venue-input-field'
                          style={{
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            flex: 1,
                            minWidth: '120px',
                            color: isDark ? '#ffffff' : '#181C32',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeField === 'timings' && (
          <div
            key={activeSubStep}
            className={`step2-substep-content step2-timings-content${completing ? ' step2-substep-exit' : ''}`}
          >
            <div className='d-flex flex-column' style={{gap: '1.5rem', paddingTop: '2.5rem'}}>
              <div className='text-center px-4 mb-4'>
                <h5
                  className='mb-2'
                  style={{
                    color: isDark ? '#ffffff' : '#181C32',
                    fontWeight: 600,
                    fontSize: '1.35rem',
                  }}
                >
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_QUESTION'})}
                </h5>
              </div>

              <div
                className='step4-teachers-table-wrapper mb-4'
                style={{
                  border: `1px solid ${isDark ? '#333640' : '#e4e6ef'}`,
                  borderRadius: 10,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                }}
              >
                <table className='table align-middle mb-0 step4-teachers-table'>
                  <thead>
                    <tr>
                      <th
                        style={{
                          width: 60,
                          background: '#056EE9',
                          color: '#fff',
                          fontWeight: 600,
                          border: 'none',
                          padding: '0.75rem 1rem',
                          textAlign: 'center',
                        }}
                      >
                        #
                      </th>
                      <th
                        style={{
                          background: '#056EE9',
                          color: '#fff',
                          fontWeight: 600,
                          border: 'none',
                          padding: '0.75rem 1rem',
                          textAlign: 'center',
                        }}
                      >
                        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_DAY'})}
                      </th>
                      <th
                        style={{
                          background: '#056EE9',
                          color: '#fff',
                          fontWeight: 600,
                          border: 'none',
                          padding: '0.75rem 1rem',
                          textAlign: 'center',
                        }}
                      >
                        {intl.formatMessage({
                          id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_INSTITUTE',
                        })}
                      </th>
                      <th
                        style={{
                          background: '#056EE9',
                          color: '#fff',
                          fontWeight: 600,
                          border: 'none',
                          padding: '0.75rem 1rem',
                          textAlign: 'center',
                        }}
                      >
                        {intl.formatMessage({
                          id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_LECTURE_DURATION',
                        })}
                      </th>
                      <th
                        style={{
                          width: 100,
                          background: '#056EE9',
                          color: '#fff',
                          fontWeight: 600,
                          border: 'none',
                          padding: '0.75rem 1rem',
                          textAlign: 'center',
                        }}
                      >
                        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_ACTIONS'})}
                      </th>
                    </tr>
                  </thead>
                  <tbody style={{backgroundColor: isDark ? '#1E1E2D' : '#ffffff'}}>
                    {timings.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className='step4-teachers-empty'
                          style={{
                            backgroundColor: isDark ? '#1E1E2D' : '#ffffff',
                            border: 'none',
                            padding: '1.5rem 1rem 1rem',
                            verticalAlign: 'middle',
                          }}
                        >
                          <div className='d-flex flex-column align-items-center gap-2'>
                            <span
                              style={{
                                color: isDark ? '#A7A8AA' : '#7E8299',
                                fontSize: '0.95rem',
                              }}
                            >
                              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_EMPTY'})}
                            </span>
                            <button
                              type='button'
                              className='step4-add-row-btn d-inline-flex align-items-center gap-2'
                              onClick={() => {
                                setEditingIndex(null)
                                setTimingModalError(null)
                                setNewTiming({
                                  selectedDays: getDefaultTimingDays(),
                                  startTime: DEFAULT_TIMING_START,
                                  endTime: DEFAULT_TIMING_END,
                                  lectureDuration: DEFAULT_LECTURE_DURATION,
                                })
                                setTimingModalOpen(true)
                              }}
                            >
                              <span
                                className='step4-add-row-icon'
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  background: '#056EE9',
                                  color: '#fff',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '1rem',
                                  fontWeight: 700,
                                  lineHeight: 1,
                                }}
                              >
                                +
                              </span>
                              {intl.formatMessage({
                                id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_ADD_BTN',
                              })}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      timings.map((t, idx) => (
                        <tr key={idx}>
                          <td
                            style={{
                              color: isDark ? '#A7A8AA' : '#7E8299',
                              fontWeight: 600,
                              borderColor: isDark ? '#333640' : '#e4e6ef',
                              padding: '0.75rem 1rem',
                              textAlign: 'center',
                            }}
                          >
                            {idx + 1}
                          </td>
                          <td
                            style={{
                              color: isDark ? '#ffffff' : '#181C32',
                              borderColor: isDark ? '#333640' : '#e4e6ef',
                              padding: '0.75rem 1rem',
                              textAlign: 'center',
                            }}
                          >
                            {intl.formatMessage({id: `DAY.${t.day}` as any})}
                          </td>
                          <td
                            style={{
                              color: isDark ? '#ffffff' : '#181C32',
                              borderColor: isDark ? '#333640' : '#e4e6ef',
                              padding: '0.75rem 1rem',
                              textAlign: 'center',
                            }}
                          >
                            {formatTime24to12(t.startTime)} — {formatTime24to12(t.endTime)}
                          </td>
                          <td
                            style={{
                              color: isDark ? '#ffffff' : '#181C32',
                              borderColor: isDark ? '#333640' : '#e4e6ef',
                              padding: '0.75rem 1rem',
                              textAlign: 'center',
                            }}
                          >
                            {t.lectureDuration ?? 0}{' '}
                            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MINS'})}
                          </td>
                          <td
                            style={{
                              borderColor: isDark ? '#333640' : '#e4e6ef',
                              padding: '0.75rem 1rem',
                              textAlign: 'center',
                            }}
                          >
                            <div className='d-flex gap-2 justify-content-center'>
                              <button
                                type='button'
                                className='btn btn-sm btn-icon step2-timing-edit-btn'
                                onClick={() => handleEditTiming(idx)}
                                aria-label='Edit'
                                style={{padding: '0.35rem'}}
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                type='button'
                                className='btn btn-sm btn-icon btn-light-danger'
                                onClick={() => handleRemoveTiming(idx)}
                                aria-label='Delete'
                                style={{padding: '0.35rem'}}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                {timings.length > 0 && !allTimingDaysAdded && (
                  <div
                    className='step4-add-row-below d-flex justify-content-center p-3'
                    style={{
                      borderTop: `1px solid ${isDark ? '#333640' : '#e4e6ef'}`,
                      backgroundColor: isDark ? '#1E1E2D' : '#ffffff',
                    }}
                  >
                    <button
                      type='button'
                      className='step4-add-row-btn d-inline-flex align-items-center gap-2'
                      onClick={() => {
                        setEditingIndex(null)
                        setTimingModalError(null)
                        setNewTiming({
                          selectedDays: getDefaultTimingDays(),
                          startTime: DEFAULT_TIMING_START,
                          endTime: DEFAULT_TIMING_END,
                          lectureDuration: DEFAULT_LECTURE_DURATION,
                        })
                        setTimingModalOpen(true)
                      }}
                    >
                      <span
                        className='step4-add-row-icon'
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          background: '#056EE9',
                          color: '#fff',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1rem',
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        +
                      </span>
                      {intl.formatMessage({
                        id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_ADD_BTN',
                      })}
                    </button>
                  </div>
                )}
              </div>

              {timings.length > 0 && (
                <p
                  className='mb-0 text-center'
                  style={{
                    color: isDark ? '#92929f' : '#7E8299',
                    fontSize: '1rem',
                    lineHeight: 1.5,
                  }}
                >
                  {intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_APPLIED_INFO',
                  })}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Break timings — their own section now that the Institution Setup review
            page lists Breaks separately; markup unchanged from when it lived under
            the Timings sub-step (same click-to-add flow and break modal). */}
        {activeField === 'breaks' && (
          <div
            key={activeSubStep}
            className={`step2-substep-content step2-timings-content${completing ? ' step2-substep-exit' : ''}`}
          >
            <div className='d-flex flex-column' style={{gap: '1rem', paddingTop: '1rem'}}>
              <h6
                className='mb-3 text-center'
                style={{
                  color: isDark ? '#ffffff' : '#181C32',
                  fontWeight: 600,
                  fontSize: '1.35rem',
                }}
              >
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_SECTION_TITLE',
                })}
              </h6>
              <div
                className='step4-teachers-table-wrapper mb-4'
                style={{
                  border: `1px solid ${isDark ? '#333640' : '#e4e6ef'}`,
                  borderRadius: 10,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                }}
              >
                <table className='table align-middle mb-0 step4-teachers-table'>
                  <thead>
                    <tr>
                      <th
                        style={{
                          width: 60,
                          background: '#056EE9',
                          color: '#fff',
                          fontWeight: 600,
                          border: 'none',
                          padding: '0.75rem 1rem',
                          textAlign: 'center',
                        }}
                      >
                        #
                      </th>
                      <th
                        style={{
                          background: '#056EE9',
                          color: '#fff',
                          fontWeight: 600,
                          border: 'none',
                          padding: '0.75rem 1rem',
                          textAlign: 'center',
                        }}
                      >
                        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_DAY'})}
                      </th>
                      <th
                        style={{
                          background: '#056EE9',
                          color: '#fff',
                          fontWeight: 600,
                          border: 'none',
                          padding: '0.75rem 1rem',
                          textAlign: 'center',
                        }}
                      >
                        {intl.formatMessage({
                          id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_COLUMN',
                        })}
                      </th>
                      <th
                        style={{
                          width: 100,
                          background: '#056EE9',
                          color: '#fff',
                          fontWeight: 600,
                          border: 'none',
                          padding: '0.75rem 1rem',
                          textAlign: 'center',
                        }}
                      >
                        {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_ACTIONS'})}
                      </th>
                    </tr>
                  </thead>
                  <tbody style={{backgroundColor: isDark ? '#1E1E2D' : '#ffffff'}}>
                    {!hasAnyBreakTimings ? (
                      <tr>
                        <td
                          colSpan={4}
                          className='step4-teachers-empty'
                          style={{
                            backgroundColor: isDark ? '#1E1E2D' : '#ffffff',
                            border: 'none',
                            padding: '1.5rem 1rem 1rem',
                            verticalAlign: 'middle',
                          }}
                        >
                          <div className='d-flex flex-column align-items-center gap-2'>
                            <span
                              style={{
                                color: isDark ? '#A7A8AA' : '#7E8299',
                                fontSize: '0.95rem',
                              }}
                            >
                              {intl.formatMessage({
                                id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_EMPTY',
                              })}
                            </span>
                            <button
                              type='button'
                              className='step4-add-row-btn d-inline-flex align-items-center gap-2'
                              onClick={() => {
                                setEditingBreakDay(null)
                                setBreakTimingModalError(null)
                                setPendingBreakTimings([])
                                const prefill = getMidpointBreak(timings)
                                setNewBreakTiming({
                                  selectedDays: [],
                                  startTime: prefill.startTime,
                                  endTime: prefill.endTime,
                                })
                                setBreakTimingModalOpen(true)
                              }}
                            >
                              <span
                                className='step4-add-row-icon'
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: '50%',
                                  background: '#056EE9',
                                  color: '#fff',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '1rem',
                                  fontWeight: 700,
                                  lineHeight: 1,
                                }}
                              >
                                +
                              </span>
                              {intl.formatMessage({
                                id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_ADD_BTN',
                              })}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      breakTimingsGroupedByDay.map(([day, breaks], rowIdx) => (
                        <tr key={day}>
                          <td
                            style={{
                              color: isDark ? '#A7A8AA' : '#7E8299',
                              fontWeight: 600,
                              borderColor: isDark ? '#333640' : '#e4e6ef',
                              padding: '0.75rem 1rem',
                              textAlign: 'center',
                            }}
                          >
                            {rowIdx + 1}
                          </td>
                          <td
                            style={{
                              color: isDark ? '#ffffff' : '#181C32',
                              borderColor: isDark ? '#333640' : '#e4e6ef',
                              padding: '0.75rem 1rem',
                              textAlign: 'center',
                            }}
                          >
                            {intl.formatMessage({id: `DAY.${day}` as any})}
                          </td>
                          <td
                            style={{
                              color: isDark ? '#ffffff' : '#181C32',
                              borderColor: isDark ? '#333640' : '#e4e6ef',
                              padding: '0.75rem 1rem',
                              textAlign: 'center',
                            }}
                          >
                            {breaks
                              .map(
                                (b) =>
                                  `${formatTime24to12(b.startTime)} — ${formatTime24to12(b.endTime)}`
                              )
                              .join(', ')}
                          </td>
                          <td
                            style={{
                              borderColor: isDark ? '#333640' : '#e4e6ef',
                              padding: '0.75rem 1rem',
                              textAlign: 'center',
                            }}
                          >
                            <div className='d-flex gap-2 justify-content-center'>
                              <button
                                type='button'
                                className='btn btn-sm btn-icon step2-timing-edit-btn'
                                onClick={() => handleEditBreakTiming(day)}
                                aria-label='Edit'
                                style={{padding: '0.35rem'}}
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                type='button'
                                className='btn btn-sm btn-icon btn-light-danger'
                                onClick={() => handleRemoveBreakTimingByDay(day)}
                                aria-label='Delete'
                                style={{padding: '0.35rem'}}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                {hasAnyBreakTimings && (
                  <div
                    className='step4-add-row-below d-flex justify-content-center p-3'
                    style={{
                      borderTop: `1px solid ${isDark ? '#333640' : '#e4e6ef'}`,
                      backgroundColor: isDark ? '#1E1E2D' : '#ffffff',
                    }}
                  >
                    <button
                      type='button'
                      className='step4-add-row-btn d-inline-flex align-items-center gap-2'
                      onClick={() => {
                        setEditingBreakDay(null)
                        setBreakTimingModalError(null)
                        setPendingBreakTimings([])
                        const prefill = getMidpointBreak(timings)
                        setNewBreakTiming({
                          selectedDays: [],
                          startTime: prefill.startTime,
                          endTime: prefill.endTime,
                        })
                        setBreakTimingModalOpen(true)
                      }}
                    >
                      <span
                        className='step4-add-row-icon'
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          background: '#056EE9',
                          color: '#fff',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1rem',
                          fontWeight: 700,
                          lineHeight: 1,
                        }}
                      >
                        +
                      </span>
                      {intl.formatMessage({
                        id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_ADD_BTN',
                      })}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeField === 'frequency' && (
          <div
            key={activeSubStep}
            className={`step2-substep-content step2-frequency-content${completing ? ' step2-substep-exit' : ''}`}
          >
            <div
              className='d-flex flex-column align-items-center text-center'
              style={{gap: '1.5rem', paddingTop: '2.5rem'}}
            >
              <div className='d-flex flex-column' style={{gap: '0.35rem'}}>
                <p
                  className='mb-0 px-4'
                  style={{
                    color: isDark ? '#ffffff' : '#181C32',
                    fontSize: '1.35rem',
                    fontWeight: 600,
                    lineHeight: 1.7,
                  }}
                >
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.FREQUENCY_TITLE'})}
                </p>
                <p
                  className='mb-0 px-4'
                  style={{
                    color: isDark ? '#92929f' : '#7E8299',
                    fontSize: '0.98rem',
                    lineHeight: 1.6,
                  }}
                >
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.FREQUENCY_INSTRUCTION'})}
                </p>
              </div>
              <div className='d-flex align-items-center justify-content-center gap-2 flex-wrap'>
                <input
                  type='number'
                  min={1}
                  max={20}
                  className='form-control step2-frequency-input'
                  value={defaultSubjectFrequency || ''}
                  placeholder=''
                  inputMode='none'
                  onKeyDown={(e) => {
                    // frequency changes via the up/down stepper only — block manual typing
                    if (!['ArrowUp', 'ArrowDown', 'Tab', 'Enter'].includes(e.key)) {
                      e.preventDefault()
                    }
                  }}
                  onPaste={(e) => e.preventDefault()}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10)
                    const n = isNaN(v) ? 0 : Math.max(0, Math.min(20, v))
                    updateData({
                      manualData: {...data.manualData, defaultSubjectFrequency: n || undefined},
                    })
                  }}
                  style={{
                    background: isDark ? '#1e1e2e' : '#ffffff',
                    color: isDark ? '#ffffff' : '#181C32',
                    border: `1px solid ${isDark ? '#3f4254' : '#e4e6ef'}`,
                    borderRadius: 12,
                    width: 60,
                    padding: '0.75rem 0.5rem',
                    caretColor: 'transparent',
                  }}
                />
                <span style={{color: isDark ? '#ffffff' : '#181C32', fontSize: '1rem'}}>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_PER'})}
                </span>
                <FilterSelect
                  value={defaultFrequencyPer || 'week'}
                  options={[
                    {
                      value: 'week',
                      label: intl.formatMessage({
                        id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_FREQUENCY_PER_WEEK',
                      }),
                    },
                    {
                      value: 'day',
                      label: intl.formatMessage({
                        id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_FREQUENCY_PER_DAY',
                      }),
                    },
                  ]}
                  onChange={(v) =>
                    updateData({
                      manualData: {
                        ...data.manualData,
                        defaultFrequencyPer: v as 'week' | 'day',
                      },
                    })
                  }
                  triggerClassName='step2-frequency-select-trigger'
                  placement='under'
                  ariaLabel={intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_PER'})}
                />
              </div>
              {/* Previous:
              <p
                className='mt-2 text-center px-4'
                style={{
                  color: isDark ? '#92929f' : '#7E8299',
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                }}
              >
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.FREQUENCY_APPLIED_INFO'})}
              </p>
              */}
            </div>
          </div>
        )}
      </div>

      {/* Add Timing Modal */}
      <Modal
        show={timingModalOpen}
        backdrop='static'
        keyboard={false}
        onHide={() => {
          setTimingModalOpen(false)
          setEditingIndex(null)
          setTimingModalError(null)
        }}
        centered
        contentClassName={isDark ? 'bg-body-dark' : ''}
      >
        <Modal.Header closeButton closeVariant={isDark ? 'white' : undefined}>
          <Modal.Title style={{color: isDark ? '#ffffff' : '#181C32'}}>
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='d-flex flex-column gap-4'>
            <div>
              <label
                className='form-label mb-1 d-block'
                style={{color: isDark ? '#e4e6ef' : '#181C32'}}
              >
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_DAYS_LABEL'})}{' '}
                <span style={{color: '#dc3545'}}>*</span>
              </label>
              <p
                className='mb-3'
                style={{color: isDark ? '#92929f' : '#7E8299', fontSize: '0.875rem'}}
              >
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_DAYS_HELPER'})}
              </p>
              <div className='d-flex gap-2 justify-content-center flex-wrap'>
                {WEEKDAYS.map((d, i) => (
                  <button
                    key={d}
                    type='button'
                    className='step2-timing-day-btn'
                    onClick={() => {
                      if (isTimingDayDisabled(d)) return
                      setTimingModalError(null)
                      toggleDay(d)
                    }}
                    disabled={isTimingDayDisabled(d)}
                    title={
                      isTimingDayDisabled(d) && editingIndex === null
                        ? intl.formatMessage({
                            id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_DUPLICATE_ERROR',
                          })
                        : undefined
                    }
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      border: '2px solid',
                      borderColor: newTiming.selectedDays.includes(d)
                        ? 'var(--primary-color, #056EE9)'
                        : isTimingDayDisabled(d)
                          ? isDark
                            ? '#2f3136'
                            : '#e9ecef'
                          : isDark
                            ? '#3f4254'
                            : '#e4e6ef',
                      background: newTiming.selectedDays.includes(d)
                        ? 'var(--primary-color, #056EE9)'
                        : 'transparent',
                      color: newTiming.selectedDays.includes(d)
                        ? '#ffffff'
                        : isTimingDayDisabled(d)
                          ? isDark
                            ? '#5f6370'
                            : '#bfc3c7'
                          : isDark
                            ? '#92929f'
                            : '#7E8299',
                      opacity: isTimingDayDisabled(d) ? 0.5 : 1,
                      cursor: isTimingDayDisabled(d) ? 'not-allowed' : 'pointer',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                    }}
                  >
                    {DAY_LETTERS[i]}
                  </button>
                ))}
              </div>
              {newTiming.selectedDays.length === 0 && (
                <p
                  className='mb-0 mt-2 text-center'
                  style={{color: '#dc3545', fontSize: '0.875rem', fontWeight: 600}}
                >
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_NO_DAY_ERROR'})}
                </p>
              )}
            </div>
            <div>
              <label className='form-label' style={{color: isDark ? '#e4e6ef' : '#181C32'}}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_START'})}{' '}
                <span style={{color: '#dc3545'}}>*</span>
              </label>
              <input
                type='time'
                className='form-control'
                value={newTiming.startTime}
                onChange={(e) => {
                  setNewTiming((s) => ({...s, startTime: e.target.value}))
                  setTimingModalError(null)
                }}
                style={{
                  cursor: 'pointer',
                  background: isDark ? '#1e1e2e' : '#ffffff',
                  color: isDark ? '#ffffff' : '#181C32',
                  borderColor: isDark ? '#3f4254' : '#e4e6ef',
                }}
              />
            </div>
            <div>
              <label className='form-label' style={{color: isDark ? '#e4e6ef' : '#181C32'}}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_END'})}{' '}
                <span style={{color: '#dc3545'}}>*</span>
              </label>
              <input
                type='time'
                className='form-control'
                value={newTiming.endTime}
                onChange={(e) => {
                  setNewTiming((s) => ({...s, endTime: e.target.value}))
                  setTimingModalError(null)
                }}
                style={{
                  cursor: 'pointer',
                  background: isDark ? '#1e1e2e' : '#ffffff',
                  color: isDark ? '#ffffff' : '#181C32',
                  borderColor: isDark ? '#3f4254' : '#e4e6ef',
                }}
              />
            </div>
            <div>
              <label className='form-label' style={{color: isDark ? '#e4e6ef' : '#181C32'}}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_LECTURE_DURATION'})}{' '}
                <span style={{color: '#dc3545'}}>*</span>
              </label>
              <div className='d-flex align-items-center gap-2'>
                <input
                  type='number'
                  min={1}
                  max={240}
                  className='form-control'
                  value={newTiming.lectureDuration || ''}
                  placeholder=''
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10)
                    setNewTiming((s) => ({
                      ...s,
                      lectureDuration: isNaN(v) ? 0 : Math.max(0, Math.min(240, v)),
                    }))
                    setTimingModalError(null)
                  }}
                  style={{
                    background: isDark ? '#1e1e2e' : '#ffffff',
                    color: isDark ? '#ffffff' : '#181C32',
                    borderColor: isDark ? '#3f4254' : '#e4e6ef',
                    width: 100,
                  }}
                />
                <span style={{color: isDark ? '#92929f' : '#7E8299', fontSize: '0.95rem'}}>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MINS'})}
                </span>
              </div>
            </div>
            {timingModalError && (
              <p
                className='mb-0 mt-2'
                style={{color: '#dc3545', fontSize: '0.875rem', fontWeight: 600}}
              >
                {timingModalError}
              </p>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type='button'
            className='btn btn-light'
            onClick={() => {
              setTimingModalOpen(false)
              setEditingIndex(null)
            }}
          >
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_CANCEL'})}
          </button>
          <button
            type='button'
            className='btn btn-primary'
            onClick={handleAddTiming}
            disabled={
              newTiming.selectedDays.length === 0 ||
              !newTiming.startTime ||
              !newTiming.endTime ||
              !newTiming.lectureDuration ||
              newTiming.lectureDuration < 1
            }
          >
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_SAVE'})}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Add Break Timing Modal */}
      <Modal
        show={breakTimingModalOpen}
        backdrop='static'
        keyboard={false}
        onHide={() => {
          setBreakTimingModalOpen(false)
          setEditingBreakDay(null)
          setBreakTimingModalError(null)
          setPendingBreakTimings([])
        }}
        centered
        contentClassName={isDark ? 'bg-body-dark' : ''}
      >
        <Modal.Header closeButton closeVariant={isDark ? 'white' : undefined}>
          <Modal.Title style={{color: isDark ? '#ffffff' : '#181C32'}}>
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_MODAL_TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='d-flex flex-column gap-4'>
            {newBreakTiming.selectedDays.length > 0 &&
              (() => {
                const forSelectedDays = (rows: any[]) =>
                  rows.filter((bt: any) =>
                    newBreakTiming.selectedDays.includes((bt.day ?? '').toString())
                  )
                // Saved breaks vs. ones queued via "Add another" in this modal — the latter only
                // land in the wizard data when the user clicks Save, so label them as unsaved.
                const savedForSelectedDays = forSelectedDays(breakTimings)
                const pendingForSelectedDays = forSelectedDays(pendingBreakTimings)
                if (savedForSelectedDays.length === 0 && pendingForSelectedDays.length === 0) {
                  return null
                }
                const renderRow = (bt: any, i: number, unsaved: boolean) => (
                  <li key={i}>
                    {intl.formatMessage({id: `DAY.${bt.day}` as any})}:{' '}
                    {formatTime24to12(bt.startTime ?? '')} — {formatTime24to12(bt.endTime ?? '')}
                    {unsaved && (
                      <span className='badge badge-light-warning ms-2 fw-semibold'>
                        {intl.formatMessage({
                          id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_PENDING_BADGE',
                        })}
                      </span>
                    )}
                  </li>
                )
                return (
                  <div
                    className='rounded p-3 d-flex flex-column gap-3'
                    style={{
                      background: isDark ? '#1a1a27' : '#f5f8fa',
                      border: `1px solid ${isDark ? '#3f4254' : '#e4e6ef'}`,
                    }}
                  >
                    {pendingForSelectedDays.length > 0 && (
                      <div>
                        <label
                          className='form-label mb-2 d-block'
                          style={{color: isDark ? '#e4e6ef' : '#181C32', fontSize: '0.85rem'}}
                        >
                          {intl.formatMessage({
                            id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_PENDING_FOR_DAY',
                          })}
                        </label>
                        <ul
                          className='mb-0 ps-3'
                          style={{color: isDark ? '#a1a5b7' : '#7E8299', fontSize: '0.9rem'}}
                        >
                          {pendingForSelectedDays.map((bt: any, i: number) =>
                            renderRow(bt, i, true)
                          )}
                        </ul>
                      </div>
                    )}
                    {savedForSelectedDays.length > 0 && (
                      <div>
                        <label
                          className='form-label mb-2 d-block'
                          style={{color: isDark ? '#e4e6ef' : '#181C32', fontSize: '0.85rem'}}
                        >
                          {intl.formatMessage({
                            id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_EXISTING_FOR_DAY',
                          })}
                        </label>
                        <ul
                          className='mb-0 ps-3'
                          style={{color: isDark ? '#a1a5b7' : '#7E8299', fontSize: '0.9rem'}}
                        >
                          {savedForSelectedDays.map((bt: any, i: number) =>
                            renderRow(bt, i, false)
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )
              })()}
            <div>
              <label
                className='form-label mb-2 d-block'
                style={{color: isDark ? '#e4e6ef' : '#181C32'}}
              >
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_DAY'})}{' '}
                <span style={{color: '#dc3545'}}>*</span>
              </label>
              <div className='d-flex gap-2 justify-content-center flex-wrap'>
                {WEEKDAYS.map((d, i) => (
                  <button
                    key={d}
                    type='button'
                    className='step2-timing-day-btn'
                    onClick={() => {
                      if (!availableTimingDays.has(normalizeDay(d))) return
                      setBreakTimingModalError(null)
                      toggleBreakDay(d)
                    }}
                    disabled={!availableTimingDays.has(normalizeDay(d))}
                    title={
                      !availableTimingDays.has(normalizeDay(d))
                        ? intl.formatMessage({
                            id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_DAY_DISABLED',
                          })
                        : undefined
                    }
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      border: '2px solid',
                      borderColor: newBreakTiming.selectedDays.includes(d)
                        ? 'var(--primary-color, #056EE9)'
                        : !availableTimingDays.has(normalizeDay(d))
                          ? isDark
                            ? '#2f3136'
                            : '#e9ecef'
                          : isDark
                            ? '#3f4254'
                            : '#e4e6ef',
                      background: newBreakTiming.selectedDays.includes(d)
                        ? 'var(--primary-color, #056EE9)'
                        : !availableTimingDays.has(normalizeDay(d))
                          ? 'transparent'
                          : 'transparent',
                      color: newBreakTiming.selectedDays.includes(d)
                        ? '#ffffff'
                        : !availableTimingDays.has(normalizeDay(d))
                          ? isDark
                            ? '#5f6370'
                            : '#bfc3c7'
                          : isDark
                            ? '#92929f'
                            : '#7E8299',
                      opacity: !availableTimingDays.has(normalizeDay(d)) ? 0.5 : 1,
                      cursor: !availableTimingDays.has(normalizeDay(d)) ? 'not-allowed' : 'pointer',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                    }}
                  >
                    {DAY_LETTERS[i]}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className='form-label' style={{color: isDark ? '#e4e6ef' : '#181C32'}}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_START'})}{' '}
                <span style={{color: '#dc3545'}}>*</span>
              </label>
              <input
                type='time'
                step='60'
                className='form-control'
                value={newBreakTiming.startTime}
                onChange={(e) => {
                  setNewBreakTiming((s) => ({...s, startTime: e.target.value}))
                  setBreakTimingModalError(null)
                }}
                style={{
                  cursor: 'pointer',
                  background: isDark ? '#1e1e2e' : '#ffffff',
                  color: isDark ? '#ffffff' : '#181C32',
                  borderColor: isDark ? '#3f4254' : '#e4e6ef',
                }}
              />
            </div>
            <div>
              <label className='form-label' style={{color: isDark ? '#e4e6ef' : '#181C32'}}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_END'})}{' '}
                <span style={{color: '#dc3545'}}>*</span>
              </label>
              <input
                type='time'
                step='60'
                className='form-control'
                value={newBreakTiming.endTime}
                onChange={(e) => {
                  setNewBreakTiming((s) => ({...s, endTime: e.target.value}))
                  setBreakTimingModalError(null)
                }}
                style={{
                  cursor: 'pointer',
                  background: isDark ? '#1e1e2e' : '#ffffff',
                  color: isDark ? '#ffffff' : '#181C32',
                  borderColor: isDark ? '#3f4254' : '#e4e6ef',
                }}
              />
            </div>
            {breakTimingModalError && (
              <p
                className='mb-0 mt-2'
                style={{color: '#dc3545', fontSize: '0.875rem', fontWeight: 600}}
              >
                {breakTimingModalError}
              </p>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type='button'
            className='btn btn-light'
            onClick={() => {
              setBreakTimingModalOpen(false)
              setEditingBreakDay(null)
              setPendingBreakTimings([])
            }}
          >
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_CANCEL'})}
          </button>
          {editingBreakDay !== null ? (
            <button
              type='button'
              className='btn btn-primary'
              onClick={() => handleAddBreakTiming()}
              disabled={
                newBreakTiming.selectedDays.length === 0 ||
                !newBreakTiming.startTime ||
                !newBreakTiming.endTime
              }
            >
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_MODAL_SAVE'})}
            </button>
          ) : (
            <>
              <button
                type='button'
                className='btn btn-light-primary'
                onClick={() => handleAddBreakTiming(true)}
                disabled={
                  newBreakTiming.selectedDays.length === 0 ||
                  !newBreakTiming.startTime ||
                  !newBreakTiming.endTime
                }
              >
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_ADD_AND_ANOTHER'})}
              </button>
              <button
                type='button'
                className='btn btn-primary'
                onClick={handleSaveAndCloseBreakModal}
                disabled={
                  (newBreakTiming.selectedDays.length === 0 ||
                    !newBreakTiming.startTime ||
                    !newBreakTiming.endTime) &&
                  breakTimings.length === 0 &&
                  pendingBreakTimings.length === 0
                }
              >
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.TIMINGS_BREAK_SAVE_AND_CLOSE'})}
              </button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Back / Next Step buttons */}
      <div className='d-flex justify-content-end gap-3 mt-3'>
        {(activeSubStep > 0 || !!onBack) && (
          <button
            type='button'
            className='onboarding-btn-back me-auto'
            disabled={completing || isSubmitting}
            onClick={goBackSubStep}
          >
            <ArrowLeft size={16} className='me-2' />
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.FOOTER.BACK'})}
          </button>
        )}
        <button
          type='button'
          className='onboarding-btn-next'
          disabled={
            completing ||
            isSubmitting ||
            (activeField === 'branches' && branches.length === 0) ||
            (activeField === 'curriculum' && selectedCurriculums.length === 0) ||
            (activeField === 'levels' &&
              selectedCurriculums.some((currName) => {
                const cData = data.manualData?.levelsByCurriculum?.[currName]
                if (!cData) return true
                if (branches.length > 1 && cData.levelsSameAcrossBranches === null) return true
                const sameAcross =
                  branches.length <= 1 ? true : (cData.levelsSameAcrossBranches ?? true)
                if (sameAcross) return (cData.levels?.length ?? 0) === 0
                return branches.some((b) => (cData.levelsPerBranch?.[b]?.length ?? 0) === 0)
              })) ||
            (activeField === 'duration' &&
              (!durationName.trim() ||
                !durationStartDate ||
                !durationEndDate ||
                (durationStartDate && durationEndDate && durationEndDate <= durationStartDate))) ||
            (activeField === 'venues' &&
              (venuesSameForClass === null ||
                (venuesSameForClass === false &&
                  venues.length === 0 &&
                  (venuesAutoCount ?? 0) === 0))) ||
            (activeField === 'timings' && timings.length === 0) ||
            (activeField === 'frequency' &&
              (defaultSubjectFrequency < 1 || defaultSubjectFrequency > 20))
          }
          onClick={advanceSubStep}
        >
          {isSubmitting ? (
            <>
              <span className='spinner-border spinner-border-sm align-middle me-2' />
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP2.SAVING'})}
            </>
          ) : (
            intl.formatMessage({id: nextLabelId ?? 'ONBOARDING_WIZARD.FOOTER.NEXT'})
          )}
          <ArrowRight size={16} className='ms-2' />
        </button>
      </div>
    </div>
  )
}

export default Step2BasicSetup
